import { useState, useEffect, useRef, Fragment } from "react";
import { useLocation } from "wouter";
import {
  PhoneCall, FileText, Mail, LogOut, Trash2, Clock, User, Phone, ChevronDown, ChevronUp, RefreshCw,
  TrendingUp, BarChart3, StickyNote, Check, Loader2, X, Send, CalendarDays, Plus,
  Eye, EyeOff, Users, Monitor, Smartphone, Tablet, Globe, MousePointerClick, Route, ChevronRight, ChevronLeft,
  Building2, MapPin, Mail as MailIcon, Phone as PhoneIcon, Award, Briefcase,
  Lightbulb, Flame, Plug, Zap, Snowflake, Wind, ClipboardList, AlertTriangle, Lock, Copy,
  Download, Upload, FolderOpen, Paperclip, FileCheck2, Image as ImageIcon, Video as VideoIcon, Camera, FileImage, Menu, Pencil, Save, Bell, UserCircle2, Wrench,
  Megaphone, Palette, RotateCcw
} from "lucide-react";
import AdminNewsletter from "./admin-newsletter";
import AdminPages from "./admin-pages";
import AdminCollections from "./admin-collections";
import AdminProgrammatic from "./admin-programmatic";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from "recharts";
import logo from "/images/brand/vertex-logo.png";
import FileUploader, { type UploadedFile } from "@/components/file-uploader";

interface EventAttachment {
  id: number;
  eventId: number;
  fileName: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
}

interface NoteEntry {
  text: string;
  createdAt: string;
}

interface Submission {
  id: number;
  formType: string;
  name: string;
  email: string | null;
  phone: string | null;
  subject: string | null;
  message: string | null;
  extra: Record<string, string> | null;
  status: string | null;
  notes: NoteEntry[] | null;
  createdAt: string;
}

interface CalEvent {
  id: number;
  submissionId: number | null;
  title: string;
  description: string | null;
  eventDate: string;
  eventTime: string | null;
  eventType: string | null;
  status: string | null;
  engineer: string | null;
  createdById: number | null;
  createdByName: string | null;
  createdAt: string;
}

interface VisitorJourney {
  sessionId: string;
  device: string;
  browser: string;
  os: string;
  source: string;
  pageCount: number;
  pages: { path: string; time: string }[];
  firstSeen: string;
}

interface AnalyticsData {
  totalViews: number;
  uniqueVisitors: number;
  totalEvents: number;
  daily: { date: string; views: number; visitors: number }[];
  pages: { name: string; value: number }[];
  devices: { name: string; value: number }[];
  browsers: { name: string; value: number }[];
  os: { name: string; value: number }[];
  sources: { name: string; value: number }[];
  events: { name: string; value: number }[];
  visitors: VisitorJourney[];
}

type Status = "new" | "in_progress" | "completed" | "rejected";
type Tab = "callback" | "quote" | "contact";
type NlSubView = "nl-dashboard" | "nl-subscribers" | "nl-templates" | "nl-campaigns" | "nl-smtp" | "nl-reports" | "nl-settings";
type SidebarView = "home" | "forms" | "ppm" | "callouts" | "clients" | "company" | "calendar" | "analytics" | "profile" | "pages" | "collections" | "templates" | NlSubView;

interface CustomerRow {
  id: number;
  email: string;
  name: string;
  phone: string | null;
  address: string | null;
  createdAt: string;
  submissionCount: number;
}

const STATUS_CONFIG: Record<Status, { label: string; color: string; bg: string; border: string }> = {
  new: { label: "New", color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
  in_progress: { label: "In Progress", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  completed: { label: "Completed", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  rejected: { label: "Rejected", color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
};

const COLORS = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];

const DEVICE_ICONS: Record<string, typeof Monitor> = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

const EVENT_TYPE_COLORS: Record<string, string> = {
  appointment: "bg-blue-500",
  callback: "bg-emerald-500",
  survey: "bg-amber-500",
  installation: "bg-purple-500",
  repair: "bg-red-500",
  maintenance: "bg-cyan-500",
  callout: "bg-rose-600",
  ppm: "bg-teal-600",
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  appointment: "Appointment",
  callback: "Callback",
  survey: "Site Survey",
  installation: "Installation",
  repair: "Repair",
  maintenance: "Maintenance",
  callout: "Call Out",
  ppm: "PPM",
};

function classifySubmission(formType: string): Tab {
  const ft = formType.toLowerCase();
  if (ft.includes("callback")) return "callback";
  if (ft.includes("survey") || ft.includes("quote")) return "quote";
  return "contact";
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatusBadge({ status }: { status: Status }) {
  const config = STATUS_CONFIG[status];
  return <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${config.color} ${config.bg} ${config.border}`}>{config.label}</span>;
}

interface SubAttachment { id: number; fileName: string; mimeType: string; fileSize: number; uploadedAt: string }

function SubmissionCard({ sub, onDelete, onUpdate, onSchedule }: {
  sub: Submission; onDelete: (id: number) => void; onUpdate: (id: number, data: Partial<Submission>) => void; onSchedule: (sub: Submission) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [notesList, setNotesList] = useState<NoteEntry[]>(Array.isArray(sub.notes) ? sub.notes : []);
  const [savingNote, setSavingNote] = useState(false);
  const [attachments, setAttachments] = useState<SubAttachment[]>([]);

  useEffect(() => {
    if (!expanded) return;
    let active = true;
    fetch(`${import.meta.env.BASE_URL}api/admin/submissions/${sub.id}/attachments`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (active && Array.isArray(data)) setAttachments(data); })
      .catch(() => {});
    return () => { active = false; };
  }, [expanded, sub.id]);

  const downloadAttachment = (a: SubAttachment) => {
    window.open(`${import.meta.env.BASE_URL}api/admin/submissions/attachments/${a.id}/download`, "_blank");
  };

  const deleteAttachment = async (a: SubAttachment) => {
    if (!confirm(`Delete "${a.fileName}"?`)) return;
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/submissions/attachments/${a.id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) setAttachments(prev => prev.filter(x => x.id !== a.id));
    } catch {}
  };
  const [savingStatus, setSavingStatus] = useState(false);
  const currentStatus = (sub.status || "new") as Status;

  const handleDelete = async () => {
    if (!confirm("Delete this submission?")) return;
    setDeleting(true);
    try {
      await fetch(`${import.meta.env.BASE_URL}api/admin/submissions/${sub.id}`, { method: "DELETE", credentials: "include" });
      onDelete(sub.id);
    } catch { alert("Failed to delete"); }
    finally { setDeleting(false); }
  };

  const updateStatus = async (newStatus: Status) => {
    setSavingStatus(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/submissions/${sub.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) onUpdate(sub.id, { status: newStatus });
    } catch {}
    finally { setSavingStatus(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/submissions/${sub.id}/notes`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ text: noteText }),
      });
      if (res.ok) {
        const data = await res.json();
        setNotesList(data.notes);
        setNoteText("");
      }
    } catch {}
    finally { setSavingNote(false); }
  };

  const deleteNote = async (index: number) => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/submissions/${sub.id}/notes/${index}`, {
        method: "DELETE", credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setNotesList(data.notes);
      }
    } catch {}
  };

  const statusButtons: { status: Status; icon: typeof Check; label: string }[] = [
    { status: "new", icon: Mail, label: "New" },
    { status: "in_progress", icon: Loader2, label: "In Progress" },
    { status: "completed", icon: Check, label: "Completed" },
    { status: "rejected", icon: X, label: "Rejected" },
  ];

  return (
    <div className={`bg-white border rounded-xl p-5 hover:shadow-md transition-shadow ${
      currentStatus === "completed" ? "border-emerald-200" : currentStatus === "rejected" ? "border-red-200" : currentStatus === "in_progress" ? "border-amber-200" : "border-slate-200"
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <User className="w-4 h-4 text-primary shrink-0" />
            <span className="font-semibold text-foreground truncate">{sub.name}</span>
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full shrink-0">{sub.formType}</span>
            <StatusBadge status={currentStatus} />
            {notesList.length > 0 && !expanded && <span className="flex items-center gap-1 text-xs text-amber-600"><StickyNote className="w-3 h-3" /> {notesList.length} note{notesList.length > 1 ? "s" : ""}</span>}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
            {sub.phone && <a href={`tel:${sub.phone}`} className="flex items-center gap-1.5 hover:text-primary"><Phone className="w-3.5 h-3.5" /> {sub.phone}</a>}
            {sub.email && sub.email !== "callback@request.com" && <a href={`mailto:${sub.email}`} className="flex items-center gap-1.5 hover:text-primary"><Mail className="w-3.5 h-3.5" /> {sub.email}</a>}
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {formatDate(sub.createdAt)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onSchedule(sub)} className="p-2 rounded-lg hover:bg-blue-50 text-muted-foreground hover:text-blue-600 transition-colors" title="Schedule on calendar">
            <CalendarDays className="w-4 h-4" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-2 rounded-lg hover:bg-slate-100 text-muted-foreground hover:text-foreground transition-colors">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button onClick={handleDelete} disabled={deleting} className="p-2 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors disabled:opacity-50">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
          {sub.subject && <div><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Subject</span><p className="text-sm text-foreground mt-0.5">{sub.subject}</p></div>}
          {sub.message && <div><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Message</span><p className="text-sm text-foreground mt-0.5 whitespace-pre-wrap">{sub.message}</p></div>}
          {sub.extra && Object.keys(sub.extra).length > 0 && (
            <div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Details</span>
              <div className="mt-1 space-y-1">
                {Object.entries(sub.extra).map(([k, v]) => <div key={k} className="text-sm"><span className="font-medium text-foreground">{k}:</span> <span className="text-muted-foreground">{v}</span></div>)}
              </div>
            </div>
          )}
          {attachments.length > 0 && (
            <div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><Paperclip className="w-3 h-3" /> Attachments ({attachments.length})</span>
              <ul className="mt-2 space-y-1.5">
                {attachments.map(a => {
                  const isImage = a.mimeType.startsWith("image/");
                  const Icon = isImage ? ImageIcon : (a.mimeType.includes("sheet") || a.mimeType === "text/csv" || a.mimeType === "application/vnd.ms-excel") ? FileCheck2 : FileText;
                  return (
                    <li key={a.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-sm">
                      <Icon className="w-4 h-4 text-primary shrink-0" />
                      <button onClick={() => downloadAttachment(a)} className="flex-1 min-w-0 truncate text-left text-foreground hover:text-primary hover:underline">{a.fileName}</button>
                      <span className="text-xs text-muted-foreground shrink-0">{(a.fileSize / 1024).toFixed(0)} KB</span>
                      <button onClick={() => downloadAttachment(a)} className="p-1 rounded hover:bg-blue-50 text-muted-foreground hover:text-blue-600" title="Download"><Download className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteAttachment(a)} className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {statusButtons.map(btn => {
                  const Icon = btn.icon;
                  const isActive = currentStatus === btn.status;
                  const config = STATUS_CONFIG[btn.status];
                  return (
                    <button key={btn.status} onClick={() => updateStatus(btn.status)} disabled={savingStatus || isActive}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        isActive ? `${config.bg} ${config.color} ${config.border} shadow-sm` : "bg-white border-slate-200 text-muted-foreground hover:border-slate-300 hover:bg-slate-50"
                      } disabled:opacity-60`}>
                      <Icon className="w-3.5 h-3.5" /> {btn.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Notes</span>
              {notesList.length > 0 && (
                <div className="mt-2 space-y-2">
                  {notesList.map((note, idx) => (
                    <div key={idx} className="flex items-start gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground whitespace-pre-wrap">{note.text}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {new Date(note.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} at {new Date(note.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <button onClick={() => deleteNote(idx)} className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 shrink-0 mt-0.5" title="Delete note">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Write a new note..." rows={2}
                  className="flex-1 px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-y" />
                <button onClick={addNote} disabled={savingNote || !noteText.trim()}
                  className="self-end px-3 py-2 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 transition-all disabled:opacity-40 flex items-center gap-1.5 shrink-0">
                  <Send className="w-3.5 h-3.5" /> {savingNote ? "Adding..." : "Add Note"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }: { label: string; value: string | number; icon: typeof Eye; color: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground font-medium">{label}</p>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, icon: Icon, children }: { title: string; icon: typeof BarChart3; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4"><Icon className="w-4 h-4 text-primary" /><h3 className="font-semibold text-foreground text-sm">{title}</h3></div>
      <div className="h-[300px]">{children}</div>
    </div>
  );
}

function VisitorRow({ visitor }: { visitor: VisitorJourney }) {
  const [expanded, setExpanded] = useState(false);
  const DeviceIcon = DEVICE_ICONS[visitor.device] || Monitor;
  const date = visitor.firstSeen ? new Date(visitor.firstSeen).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  return (
    <div className="border border-slate-100 rounded-lg overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left">
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        <DeviceIcon className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-mono text-muted-foreground w-20 shrink-0">{visitor.sessionId}</span>
        <span className="text-sm text-foreground font-medium">{visitor.pageCount} pages</span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium shrink-0">{visitor.source}</span>
        <span className="text-xs text-muted-foreground ml-auto shrink-0">{date}</span>
      </button>
      {expanded && (
        <div className="px-4 pb-3 pt-1 bg-slate-50/50">
          <div className="flex gap-4 mb-3 text-xs text-muted-foreground"><span>{visitor.browser}</span><span>{visitor.os}</span><span className="capitalize">{visitor.device}</span></div>
          <div className="space-y-1">
            {visitor.pages.map((p, i) => {
              const isEvent = p.path.startsWith("[");
              const time = p.time ? new Date(p.time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
              return (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="text-xs text-muted-foreground w-16 shrink-0 font-mono">{time}</span>
                  {isEvent ? <><Phone className="w-3 h-3 text-orange-500 shrink-0" /><span className="text-orange-600 font-medium">{p.path}</span></> : <><div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" /><span className="text-foreground">{p.path}</span></>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleModal({ submission, onClose, onCreated }: { submission: Submission | null; onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [eventType, setEventType] = useState("appointment");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (submission) {
      const service = submission.extra?.Service || submission.extra?.service || "";
      setTitle(`${submission.name}${service ? ` — ${service}` : ""}`);
      const addr = submission.extra?.["Site Address"] || submission.extra?.Postcode || submission.extra?.postcode || "";
      setDescription(
        [submission.phone, submission.email, addr].filter(Boolean).join(" | ")
      );
      const cat = classifySubmission(submission.formType);
      setEventType(cat === "callback" ? "callback" : cat === "quote" ? "survey" : "appointment");
    }
  }, [submission]);

  if (!submission) return null;

  const handleSave = async () => {
    if (!title || !date) return;
    setSaving(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/calendar`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ submissionId: submission.id, title, description, eventDate: date, eventTime: time || null, eventType }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(body?.error || `Failed to schedule (${res.status})`);
        return;
      }
      onCreated();
      onClose();
    } catch (err) {
      alert("Failed to schedule. Please try again.");
      console.error("Schedule error", err);
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-foreground flex items-center gap-2"><CalendarDays className="w-5 h-5 text-primary" /> Schedule Appointment</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</label>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</label>
          <select value={eventType} onChange={e => setEventType(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
            <option value="appointment">Appointment</option>
            <option value="callback">Callback</option>
            <option value="survey">Site Survey</option>
            <option value="installation">Installation</option>
            <option value="repair">Repair</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Notes</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
            className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
        </div>
        <button onClick={handleSave} disabled={saving || !title || !date}
          className="w-full py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
          <CalendarDays className="w-4 h-4" /> {saving ? "Scheduling..." : "Add to Calendar"}
        </button>
      </div>
    </div>
  );
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function DayEventDetails({ ev, onComplete, onCancel, onReopen, onDelete }: {
  ev: CalEvent;
  onComplete: () => void;
  onCancel: () => void;
  onReopen: () => void;
  onDelete: () => void;
}) {
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ev.submissionId) { setSubmission(null); return; }
    let active = true;
    setLoading(true);
    fetch(`${import.meta.env.BASE_URL}api/admin/submissions`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then((all: Submission[]) => {
        if (!active) return;
        const found = Array.isArray(all) ? all.find(s => s.id === ev.submissionId) : null;
        setSubmission(found || null);
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [ev.submissionId]);

  const status = ev.status || "scheduled";
  const statusColors: Record<string, string> = {
    scheduled: "bg-blue-50 text-blue-700 border-blue-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className={`px-5 py-3 ${EVENT_TYPE_COLORS[ev.eventType || "appointment"] || "bg-slate-500"} text-white flex items-start justify-between gap-3`}>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">{ev.eventType || "appointment"}</div>
          <h4 className="font-bold text-base leading-tight mt-0.5 break-words">{ev.title}</h4>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {status !== "completed" && (
            <button onClick={onComplete} title="Mark complete" className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25"><Check className="w-4 h-4" /></button>
          )}
          {status === "scheduled" && (
            <button onClick={onCancel} title="Cancel" className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25"><X className="w-4 h-4" /></button>
          )}
          {status !== "scheduled" && (
            <button onClick={onReopen} title="Re-open" className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25"><RefreshCw className="w-4 h-4" /></button>
          )}
          <button onClick={onDelete} title="Delete" className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="p-5 space-y-4 text-sm">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Date</div>
            <div className="font-medium text-foreground">{new Date(ev.eventDate + "T00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Time</div>
            <div className="font-medium text-foreground">{ev.eventTime || "All day"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Status</div>
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full border capitalize font-medium ${statusColors[status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{status}</span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Created</div>
            <div className="text-foreground text-xs">{formatDate(ev.createdAt)}</div>
          </div>
        </div>

        {ev.description && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Notes</div>
            <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-foreground whitespace-pre-wrap break-words">{ev.description}</div>
          </div>
        )}

        {ev.submissionId && (
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Linked Enquiry #{ev.submissionId}</span>
            </div>
            {loading ? (
              <div className="text-xs text-muted-foreground italic">Loading enquiry details…</div>
            ) : submission ? (
              <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-semibold text-foreground">{submission.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-slate-200 text-muted-foreground">{submission.formType}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {submission.phone && (
                    <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-muted-foreground" /><a href={`tel:${submission.phone}`} className="text-primary hover:underline font-medium">{submission.phone}</a></div>
                  )}
                  {submission.email && (
                    <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-muted-foreground" /><a href={`mailto:${submission.email}`} className="text-primary hover:underline font-medium truncate">{submission.email}</a></div>
                  )}
                </div>
                {submission.subject && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Subject</div>
                    <div className="text-foreground">{submission.subject}</div>
                  </div>
                )}
                {submission.message && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Message</div>
                    <div className="text-foreground whitespace-pre-wrap text-xs">{submission.message}</div>
                  </div>
                )}
                {submission.extra && Object.keys(submission.extra).length > 0 && (
                  <div className="pt-2 border-t border-blue-100">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Details</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {Object.entries(submission.extra).map(([k, v]) => (
                        <div key={k}><span className="font-medium text-foreground">{k}:</span> <span className="text-muted-foreground">{String(v)}</span></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground italic">Original enquiry no longer available.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CalendarView() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalEvent | null>(null);
  const [eventSubmission, setEventSubmission] = useState<Submission | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [newType, setNewType] = useState("appointment");
  const [newDesc, setNewDesc] = useState("");
  const [newEngineer, setNewEngineer] = useState("");
  const [newFiles, setNewFiles] = useState<UploadedFile[]>([]);
  const [saving, setSaving] = useState(false);
  const [eventAttachments, setEventAttachments] = useState<EventAttachment[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editType, setEditType] = useState("appointment");
  const [editDesc, setEditDesc] = useState("");
  const [editEngineer, setEditEngineer] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [uploadingDetail, setUploadingDetail] = useState(false);
  const [showNewCallout, setShowNewCallout] = useState(false);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/calendar?month=${month + 1}&year=${year}`, { credentials: "include" });
      if (res.ok) setEvents(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEvents(); }, [month, year]);

  useEffect(() => {
    if (!selectedEvent || !selectedEvent.submissionId) { setEventSubmission(null); return; }
    let active = true;
    fetch(`${import.meta.env.BASE_URL}api/admin/submissions`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then((all: Submission[]) => {
        if (!active) return;
        const found = Array.isArray(all) ? all.find(s => s.id === selectedEvent.submissionId) : null;
        setEventSubmission(found || null);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [selectedEvent]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); setSelectedDay(null); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); setSelectedDay(null); };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const getEventsForDay = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return events.filter(e => e.eventDate === dateStr);
  };

  const selectedEvents = selectedDay ? getEventsForDay(selectedDay) : [];
  const selectedDateStr = selectedDay ? `${year}-${String(month + 1).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}` : "";

  const handleAddEvent = async () => {
    if (!newTitle || !newDate) return;
    setSaving(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/calendar`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ title: newTitle, description: newDesc || null, eventDate: newDate, eventTime: newTime || null, eventType: newType, engineer: newEngineer || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(body?.error || `Failed to add event (${res.status})`);
        return;
      }
      const created = await res.json().catch(() => null);
      const eventId = created?.id;
      if (eventId && newFiles.length > 0) {
        for (const f of newFiles) {
          try {
            await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${eventId}/attachments`, {
              method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
              body: JSON.stringify({ fileName: f.name, mimeType: f.mimeType, fileSize: f.size, data: f.data }),
            });
          } catch (e) { console.error("Attachment upload failed", e); }
        }
      }
      setNewTitle(""); setNewDate(""); setNewTime(""); setNewType("appointment"); setNewDesc(""); setNewEngineer(""); setNewFiles([]);
      setShowAdd(false);
      fetchEvents();
    } catch { alert("Failed to add event"); }
    finally { setSaving(false); }
  };

  const fetchEventAttachments = async (eventId: number) => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${eventId}/attachments`, { credentials: "include" });
      if (res.ok) setEventAttachments(await res.json());
      else setEventAttachments([]);
    } catch { setEventAttachments([]); }
  };

  useEffect(() => {
    if (selectedEvent) {
      setEditTitle(selectedEvent.title);
      setEditDate(selectedEvent.eventDate);
      setEditTime(selectedEvent.eventTime || "");
      setEditType(selectedEvent.eventType || "appointment");
      setEditDesc(selectedEvent.description || "");
      setEditEngineer(selectedEvent.engineer || "");
      setEditMode(false);
      fetchEventAttachments(selectedEvent.id);
    } else {
      setEventAttachments([]);
      setEditMode(false);
    }
  }, [selectedEvent?.id]);

  const handleSaveEdit = async () => {
    if (!selectedEvent || !editTitle || !editDate) return;
    setEditSaving(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${selectedEvent.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          title: editTitle,
          description: editDesc || null,
          eventDate: editDate,
          eventTime: editTime || null,
          eventType: editType,
          engineer: editEngineer || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(body?.error || `Failed to update (${res.status})`);
        return;
      }
      setSelectedEvent({ ...selectedEvent, title: editTitle, description: editDesc || null, eventDate: editDate, eventTime: editTime || null, eventType: editType, engineer: editEngineer || null });
      setEditMode(false);
      fetchEvents();
    } catch { alert("Failed to update event"); }
    finally { setEditSaving(false); }
  };

  const handleDetailFileUpload = async (files: UploadedFile[]) => {
    if (!selectedEvent || files.length === 0) return;
    setUploadingDetail(true);
    try {
      for (const f of files) {
        await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${selectedEvent.id}/attachments`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ fileName: f.name, mimeType: f.mimeType, fileSize: f.size, data: f.data }),
        });
      }
      await fetchEventAttachments(selectedEvent.id);
    } catch { alert("Failed to upload attachment"); }
    finally { setUploadingDetail(false); }
  };

  const handleDeleteAttachment = async (id: number) => {
    if (!confirm("Delete this attachment?")) return;
    try {
      await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/attachments/${id}`, { method: "DELETE", credentials: "include" });
      if (selectedEvent) fetchEventAttachments(selectedEvent.id);
    } catch {}
  };

  const handleDeleteEvent = async (id: number) => {
    if (!confirm("Delete this event?")) return;
    try {
      await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${id}`, { method: "DELETE", credentials: "include" });
      fetchEvents();
    } catch {}
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ status }),
      });
      fetchEvents();
    } catch {}
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-slate-100 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
          <h2 className="text-lg font-bold text-foreground">{MONTH_NAMES[month]} {year}</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => { setShowAdd(true); setNewDate(selectedDateStr || todayStr); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-all">
              <Plus className="w-4 h-4" /> Add Event
            </button>
            <button onClick={() => setShowNewCallout(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition-all">
              <AlertTriangle className="w-4 h-4" /> New Call Out
            </button>
            <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-slate-100 transition-colors"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="grid grid-cols-7">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
            <div key={d} className="text-center text-xs font-medium text-muted-foreground py-3 border-b border-slate-100">{d}</div>
          ))}
          {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} className="min-h-[100px] border-b border-r border-slate-50" />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const isToday = dateStr === todayStr;
            const dayEvents = getEventsForDay(day);
            const isSelected = selectedDay === day;
            return (
              <div key={day} onClick={() => setSelectedDay(day)}
                className={`min-h-[100px] border-b border-r border-slate-50 p-1.5 cursor-pointer transition-colors ${
                  isSelected ? "bg-primary/5 ring-2 ring-primary/20 ring-inset" : "hover:bg-slate-50"
                }`}>
                <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                  isToday ? "bg-primary text-white" : "text-foreground"
                }`}>{day}</div>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map(ev => (
                    <div key={ev.id} onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                      className={`text-[10px] px-1.5 py-0.5 rounded truncate text-white font-medium cursor-pointer hover:opacity-90 ${EVENT_TYPE_COLORS[ev.eventType || "appointment"] || "bg-slate-500"}`}>
                      {ev.eventTime ? `${ev.eventTime} ` : ""}{ev.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && <div className="text-[10px] text-muted-foreground font-medium px-1">+{dayEvents.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-semibold text-foreground mb-4">
            {new Date(year, month, selectedDay).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </h3>
          {selectedEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events scheduled for this day.</p>
          ) : (
            <div className="space-y-4">
              {selectedEvents.map(ev => (
                <DayEventDetails
                  key={ev.id}
                  ev={ev}
                  onComplete={() => handleStatusChange(ev.id, "completed")}
                  onCancel={() => handleStatusChange(ev.id, "cancelled")}
                  onReopen={() => handleStatusChange(ev.id, "scheduled")}
                  onDelete={() => handleDeleteEvent(ev.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md my-8 p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-foreground flex items-center gap-2"><Plus className="w-5 h-5 text-primary" /> New Event</h3>
              <button onClick={() => setShowAdd(false)} className="p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Title</label>
              <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Event title" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</label>
                <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</label>
                <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</label>
              <select value={newType} onChange={e => setNewType(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="appointment">Appointment</option>
                <option value="callback">Callback</option>
                <option value="survey">Site Survey</option>
                <option value="installation">Installation</option>
                <option value="repair">Repair</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Engineer</label>
              <input value={newEngineer} onChange={e => setNewEngineer(e.target.value)} placeholder="Engineer attending"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Notes</label>
              <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} rows={2} placeholder="Optional notes..."
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
            </div>
            <FileUploader files={newFiles} onChange={setNewFiles} label="Attach files (photos, PDF, Excel)" />
            <button onClick={handleAddEvent} disabled={saving || !newTitle || !newDate}
              className="w-full py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 disabled:opacity-40 transition-all">
              {saving ? "Adding..." : "Add Event"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {Object.entries(EVENT_TYPE_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className={`w-3 h-3 rounded-full ${color}`} />
            <span className="capitalize">{type}</span>
          </div>
        ))}
      </div>

      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedEvent(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className={`px-6 py-4 ${EVENT_TYPE_COLORS[selectedEvent.eventType || "appointment"] || "bg-slate-500"} text-white rounded-t-2xl flex items-start justify-between gap-3`}>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">{selectedEvent.eventType}</div>
                <h3 className="font-bold text-lg leading-tight mt-0.5 break-words">{selectedEvent.title}</h3>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!editMode && (
                  <button onClick={() => setEditMode(true)} title="Edit" className="p-1.5 rounded-lg hover:bg-white/20"><Pencil className="w-4 h-4" /></button>
                )}
                <button onClick={() => setSelectedEvent(null)} className="p-1 rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="p-6 space-y-4 text-sm">
              {editMode ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Title</label>
                    <input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Date</label>
                      <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Time</label>
                      <input type="time" value={editTime} onChange={e => setEditTime(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Type</label>
                    <select value={editType} onChange={e => setEditType(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                      <option value="appointment">Appointment</option>
                      <option value="callback">Callback</option>
                      <option value="survey">Site Survey</option>
                      <option value="installation">Installation</option>
                      <option value="repair">Repair</option>
                      <option value="maintenance">Maintenance</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Engineer</label>
                    <input value={editEngineer} onChange={e => setEditEngineer(e.target.value)} placeholder="Engineer attending" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Notes</label>
                    <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <button onClick={handleSaveEdit} disabled={editSaving || !editTitle || !editDate} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-40">
                      <Save className="w-4 h-4" /> {editSaving ? "Saving..." : "Save changes"}
                    </button>
                    <button onClick={() => setEditMode(false)} className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Date</div>
                      <div className="font-medium text-foreground">{new Date(selectedEvent.eventDate + "T00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Time</div>
                      <div className="font-medium text-foreground">{selectedEvent.eventTime || "All day"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Status</div>
                      <span className={`inline-block text-xs px-2 py-0.5 rounded-full capitalize font-medium ${
                        selectedEvent.status === "completed" ? "bg-emerald-50 text-emerald-700" :
                        selectedEvent.status === "cancelled" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"
                      }`}>{selectedEvent.status || "scheduled"}</span>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Created</div>
                      <div className="text-foreground">{formatDate(selectedEvent.createdAt)}</div>
                    </div>
                  </div>

                  {selectedEvent.description && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Notes</div>
                      <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-foreground whitespace-pre-wrap break-words">{selectedEvent.description}</div>
                    </div>
                  )}
                </>
              )}

              <div className="border-t border-slate-100 pt-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-primary" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Attachments ({eventAttachments.length})</span>
                  </div>
                  {uploadingDetail && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                </div>
                {eventAttachments.length > 0 && (
                  <ul className="space-y-1.5">
                    {eventAttachments.map(a => (
                      <li key={a.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-sm">
                        <FileText className="w-4 h-4 text-primary shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-foreground">{a.fileName}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{(a.fileSize / 1024).toFixed(0)} KB</span>
                        <a href={`${import.meta.env.BASE_URL}api/admin/calendar/attachments/${a.id}/download`} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-slate-200 text-muted-foreground hover:text-primary" title="Download"><Download className="w-3.5 h-3.5" /></a>
                        <button onClick={() => handleDeleteAttachment(a.id)} className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </li>
                    ))}
                  </ul>
                )}
                <FileUploader files={[]} onChange={handleDetailFileUpload} label={eventAttachments.length > 0 ? "Add more files" : "Add files (photos, PDF, Excel)"} />
              </div>

              {eventSubmission && (
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Linked Enquiry</span>
                  </div>
                  <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-foreground">{eventSubmission.name}</span>
                      <span className="text-xs text-muted-foreground">{eventSubmission.formType}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-1 text-xs">
                      {eventSubmission.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-muted-foreground" /><a href={`tel:${eventSubmission.phone}`} className="text-primary hover:underline">{eventSubmission.phone}</a></div>}
                      {eventSubmission.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-muted-foreground" /><a href={`mailto:${eventSubmission.email}`} className="text-primary hover:underline">{eventSubmission.email}</a></div>}
                    </div>
                    {eventSubmission.message && <div className="text-xs text-foreground whitespace-pre-wrap line-clamp-4">{eventSubmission.message}</div>}
                    {eventSubmission.extra && Object.keys(eventSubmission.extra).length > 0 && (
                      <div className="text-xs space-y-0.5 pt-1 border-t border-blue-100">
                        {Object.entries(eventSubmission.extra).map(([k, v]) => (
                          <div key={k}><span className="font-medium text-foreground">{k}:</span> <span className="text-muted-foreground">{String(v)}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                {selectedEvent.status !== "completed" && (
                  <button onClick={async () => { await handleStatusChange(selectedEvent.id, "completed"); setSelectedEvent({ ...selectedEvent, status: "completed" }); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm font-medium">
                    <Check className="w-4 h-4" /> Mark complete
                  </button>
                )}
                <button onClick={async () => { await handleDeleteEvent(selectedEvent.id); setSelectedEvent(null); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 text-sm font-medium">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <NewCallOutModal open={showNewCallout} onClose={() => setShowNewCallout(false)} onCreated={fetchEvents} defaultDate={selectedDateStr || todayStr} />
    </div>
  );
}

interface CompanyRow {
  id: number;
  tradingName: string;
  operatingCompany: string | null;
  companyNumber: string | null;
  vatNumber: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  contactName: string | null;
  notes: string | null;
  accreditations: string[] | null;
  createdAt: string;
}

function FieldInput({ label, value, onChange, type = "text", placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
      />
    </div>
  );
}

function FieldTextarea({ label, value, onChange, placeholder, rows = 3 }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-y"
      />
    </div>
  );
}

type PpmServiceType = "emergency_lighting" | "fire_alarm" | "pat_testing" | "eicr" | "refrigeration" | "hvac";

interface PpmRow {
  id: number;
  ownerType: "customer" | "company";
  ownerId: number;
  serviceType: PpmServiceType;
  lastServiced: string | null;
  dueDate: string | null;
  notes: string | null;
  status?: "active" | "na";
}

const PPM_SERVICES: { key: PpmServiceType; label: string; icon: any; color: string }[] = [
  { key: "emergency_lighting", label: "Emergency Lighting", icon: Lightbulb, color: "bg-amber-50 text-amber-600" },
  { key: "fire_alarm", label: "Fire Alarm", icon: Flame, color: "bg-red-50 text-red-600" },
  { key: "pat_testing", label: "PAT Testing", icon: Plug, color: "bg-purple-50 text-purple-600" },
  { key: "eicr", label: "EICR", icon: Zap, color: "bg-yellow-50 text-yellow-700" },
  { key: "refrigeration", label: "Refrigeration", icon: Snowflake, color: "bg-sky-50 text-sky-600" },
  { key: "hvac", label: "HVAC", icon: Wind, color: "bg-emerald-50 text-emerald-600" },
];

function ppmStatus(row: PpmRow | undefined): { label: string; tone: string } {
  if (row?.status === "na") {
    return { label: "N/A", tone: "bg-slate-200 text-slate-600 border-slate-300" };
  }
  if (!row || (!row.lastServiced && !row.dueDate)) {
    return { label: "Missing", tone: "bg-slate-100 text-slate-500 border-slate-200" };
  }
  if (!row.dueDate) return { label: "OK", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(row.dueDate); due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: "Overdue", tone: "bg-red-50 text-red-700 border-red-200" };
  if (diffDays <= 30) return { label: "Due", tone: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "OK", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function PpmSection({ ownerType, ownerId }: { ownerType: "customer" | "company"; ownerId: number }) {
  const [rows, setRows] = useState<PpmRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PpmServiceType | null>(null);
  const [form, setForm] = useState<{ lastServiced: string; dueDate: string; notes: string; status: "active" | "na" }>({ lastServiced: "", dueDate: "", notes: "", status: "active" });
  const [saving, setSaving] = useState(false);
  const [reportsFor, setReportsFor] = useState<{ key: PpmServiceType; label: string } | null>(null);
  const [reportCounts, setReportCounts] = useState<Record<string, number>>({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/ppm?ownerType=${ownerType}&ownerId=${ownerId}`, { credentials: "include" });
      if (res.ok) setRows(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  const loadCounts = async () => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/ppm/reports/counts?ownerType=${ownerType}&ownerId=${ownerId}`, { credentials: "include" });
      if (res.ok) setReportCounts(await res.json());
    } catch {}
  };

  useEffect(() => { load(); loadCounts(); }, [ownerType, ownerId]);

  const byType = new Map(rows.map(r => [r.serviceType, r]));

  const startEdit = (key: PpmServiceType) => {
    const existing = byType.get(key);
    setForm({
      lastServiced: existing?.lastServiced ? existing.lastServiced.slice(0, 10) : "",
      dueDate: existing?.dueDate ? existing.dueDate.slice(0, 10) : "",
      notes: existing?.notes || "",
      status: existing?.status === "na" ? "na" : "active",
    });
    setEditing(key);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/ppm`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ownerType, ownerId, serviceType: editing,
          lastServiced: form.lastServiced || null,
          dueDate: form.dueDate || null,
          notes: form.notes || null,
          status: form.status,
        }),
      });
      if (res.ok) {
        const saved: PpmRow = await res.json();
        setRows(prev => {
          const others = prev.filter(r => r.serviceType !== editing);
          return [...others, saved];
        });
        setEditing(null);
      }
    } catch {}
    finally { setSaving(false); }
  };

  const handleClear = async () => {
    if (!editing) return;
    const existing = byType.get(editing);
    if (!existing) { setEditing(null); return; }
    if (!confirm("Clear this PPM record?")) return;
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/ppm/${existing.id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        setRows(prev => prev.filter(r => r.id !== existing.id));
        setEditing(null);
      }
    } catch {}
  };

  const summary = {
    overdue: PPM_SERVICES.filter(s => ppmStatus(byType.get(s.key)).label === "Overdue").length,
    due: PPM_SERVICES.filter(s => ppmStatus(byType.get(s.key)).label === "Due").length,
    ok: PPM_SERVICES.filter(s => ppmStatus(byType.get(s.key)).label === "OK").length,
    missing: PPM_SERVICES.filter(s => ppmStatus(byType.get(s.key)).label === "Missing").length,
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-slate-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center">
            <ClipboardList className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-foreground">PPM Schedules</h2>
            <p className="text-xs text-muted-foreground">Planned preventive maintenance records</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs">
            {summary.overdue > 0 && <span className="px-2 py-1 rounded-full bg-red-50 text-red-700 font-semibold border border-red-200">{summary.overdue} overdue</span>}
            {summary.due > 0 && <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold border border-amber-200">{summary.due} due</span>}
            {summary.ok > 0 && <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-200">{summary.ok} ok</span>}
            {summary.missing > 0 && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold border border-slate-200">{summary.missing} missing</span>}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Service</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Last Serviced</th>
                <th className="px-4 py-3 font-semibold">Due</th>
                <th className="px-4 py-3 font-semibold">Notes</th>
                <th className="px-4 py-3 font-semibold w-44 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {PPM_SERVICES.map(svc => {
                const row = byType.get(svc.key);
                const status = ppmStatus(row);
                const isEditing = editing === svc.key;
                const Icon = svc.icon;
                return (
                  <Fragment key={svc.key}>
                    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${svc.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="font-medium text-foreground">{svc.label}</span>
                          {reportCounts[svc.key] > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold" title={`${reportCounts[svc.key]} report${reportCounts[svc.key] > 1 ? "s" : ""} on file`}>
                              <FileCheck2 className="w-3 h-3" />
                              {reportCounts[svc.key]}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-semibold ${status.tone}`}>
                          {status.label === "Overdue" && <AlertTriangle className="w-3 h-3" />}
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground/80">{fmtDate(row?.lastServiced || null)}</td>
                      <td className="px-4 py-3 text-foreground/80">{fmtDate(row?.dueDate || null)}</td>
                      <td className="px-4 py-3 max-w-[16rem]">
                        {row?.notes ? <span className="text-foreground/70 line-clamp-1" title={row.notes}>{row.notes}</span> : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => setReportsFor({ key: svc.key, label: svc.label })} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 transition-colors" title={`${svc.label} reports & certificates`}>
                            <FolderOpen className="w-3.5 h-3.5" /> Reports
                            {reportCounts[svc.key] > 0 && (
                              <span className="ml-0.5 px-1.5 rounded-full bg-sky-600 text-white text-[10px] font-bold">{reportCounts[svc.key]}</span>
                            )}
                          </button>
                          <button onClick={() => startEdit(svc.key)} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-foreground transition-colors">
                            {row ? "Edit" : "Set"}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isEditing && (
                      <tr className="bg-slate-50/60 border-b border-slate-100">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Last Serviced</div>
                              <input type="date" value={form.lastServiced} onChange={e => setForm({ ...form, lastServiced: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Due Date</div>
                              <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                            </div>
                            <div className="sm:col-span-2 lg:col-span-2">
                              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Notes</div>
                              <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                            </div>
                            <div className="sm:col-span-2 lg:col-span-4">
                              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Visibility to client</div>
                              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                                <button
                                  type="button"
                                  onClick={() => setForm({ ...form, status: "active" })}
                                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${form.status === "active" ? "bg-emerald-500 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                                >
                                  Active
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setForm({ ...form, status: "na" })}
                                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${form.status === "na" ? "bg-slate-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                                >
                                  N/A
                                </button>
                              </div>
                              <p className="mt-1.5 text-xs text-muted-foreground">
                                {form.status === "na"
                                  ? "Hidden from client / company portal."
                                  : "Visible in the client / company portal."}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-60 transition-colors flex items-center gap-2">
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
                            </button>
                            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-100 transition-colors">
                              Cancel
                            </button>
                            {row && (
                              <button onClick={handleClear} className="ml-auto px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2">
                                <Trash2 className="w-4 h-4" /> Clear record
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {reportsFor && (
        <PpmReportsModal
          ownerType={ownerType}
          ownerId={ownerId}
          serviceKey={reportsFor.key}
          serviceLabel={reportsFor.label}
          onClose={() => { setReportsFor(null); loadCounts(); }}
          onChange={loadCounts}
        />
      )}
    </div>
  );
}

type PpmReportRow = {
  id: number;
  fileName: string;
  mimeType: string;
  fileSize: number;
  reportDate: string | null;
  notes: string | null;
  uploadedAt: string;
};

function PpmReportsModal({ ownerType, ownerId, serviceKey, serviceLabel, onClose, onChange }: {
  ownerType: "customer" | "company"; ownerId: number;
  serviceKey: PpmServiceType; serviceLabel: string; onClose: () => void; onChange?: () => void;
}) {
  const [rows, setRows] = useState<PpmReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [notes, setNotes] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/ppm/reports?ownerType=${ownerType}&ownerId=${ownerId}&serviceType=${serviceKey}`, { credentials: "include" });
      if (res.ok) setRows(await res.json());
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [ownerType, ownerId, serviceKey]);

  const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      const idx = r.indexOf(",");
      resolve(idx >= 0 ? r.slice(idx + 1) : r);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const handleUpload = async () => {
    setError("");
    if (!pendingFile) { setError("Choose a file first"); return; }
    if (pendingFile.size > 15 * 1024 * 1024) { setError("File too large (max 15MB)"); return; }
    setUploading(true);
    try {
      const data = await fileToBase64(pendingFile);
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/ppm/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ownerType, ownerId, serviceType: serviceKey,
          fileName: pendingFile.name,
          mimeType: pendingFile.type || "application/octet-stream",
          fileSize: pendingFile.size,
          reportDate: reportDate || null,
          notes: notes || null,
          data,
        }),
      });
      if (!res.ok) {
        if (res.status === 401) {
          setError("Your admin session has expired. Please refresh the page and log in again.");
          return;
        }
        const d = await res.json().catch(() => ({}));
        setError(d.error || "Upload failed");
        return;
      }
      setPendingFile(null);
      setReportDate("");
      setNotes("");
      await load();
      onChange?.();
    } catch (e: any) {
      setError(e?.message || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this report?")) return;
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/ppm/reports/${id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { setRows(prev => prev.filter(r => r.id !== id)); onChange?.(); }
    } catch {}
  };

  const downloadUrl = (id: number) => `${import.meta.env.BASE_URL}api/admin/ppm/reports/${id}/download`;
  const fmtSize = (n: number) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(2)} MB`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center">
            <FolderOpen className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-foreground">{serviceLabel} — Reports & Certificates</h2>
            <p className="text-xs text-muted-foreground">Upload, view and download service reports</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/60">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Upload new report</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground/70 block mb-1.5">File (PDF, image, doc — max 15MB)</label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,image/*,application/pdf" onChange={e => setPendingFile(e.target.files?.[0] || null)} className="w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-white file:font-semibold file:cursor-pointer hover:file:bg-primary/90" />
              {pendingFile && <div className="mt-1 text-xs text-muted-foreground">{pendingFile.name} · {fmtSize(pendingFile.size)}</div>}
            </div>
            <div>
              <label className="text-xs font-medium text-foreground/70 block mb-1.5">Report Date</label>
              <input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-foreground/70 block mb-1.5">Notes (optional)</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Annual EICR — passed" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
          </div>
          {error && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="mt-3 flex justify-end">
            <button onClick={handleUpload} disabled={uploading || !pendingFile} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Upload report"}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">No reports uploaded yet for {serviceLabel}.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">File</th>
                  <th className="px-4 py-3 font-semibold">Notes</th>
                  <th className="px-4 py-3 font-semibold">Uploaded</th>
                  <th className="px-4 py-3 font-semibold w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-foreground">{fmtDate(r.reportDate)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-sky-600 shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-foreground truncate max-w-[16rem]" title={r.fileName}>{r.fileName}</div>
                          <div className="text-xs text-muted-foreground">{fmtSize(r.fileSize)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-[14rem]">
                      {r.notes ? <span className="text-foreground/70 line-clamp-1" title={r.notes}>{r.notes}</span> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.uploadedAt).toLocaleDateString("en-GB")}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <a href={downloadUrl(r.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200">
                          <Download className="w-3.5 h-3.5" /> Download
                        </a>
                        <button onClick={() => handleDelete(r.id)} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 border border-red-200" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ===================== Call Outs =====================

type CalloutPriority = "low" | "medium" | "high" | "emergency";
type CalloutStatus = "open" | "in_progress" | "resolved";

interface CalloutRow {
  id: number;
  ownerType: "customer" | "company";
  ownerId: number;
  callDate: string;
  callType: string;
  priority: CalloutPriority;
  status: CalloutStatus;
  engineer: string | null;
  description: string | null;
  resolvedDate: string | null;
  notes: string | null;
  siteAddress: string | null;
  parkingAvailable: "yes" | "no" | "unknown" | null;
  siteContactName: string | null;
  siteContactPhone: string | null;
  createdById: number | null;
  createdByName: string | null;
  createdAt: string | null;
}

const PRIORITY_TONES: Record<CalloutPriority, string> = {
  low: "bg-slate-100 text-slate-700 border-slate-200",
  medium: "bg-sky-50 text-sky-700 border-sky-200",
  high: "bg-amber-50 text-amber-700 border-amber-200",
  emergency: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_TONES: Record<CalloutStatus, string> = {
  open: "bg-red-50 text-red-700 border-red-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const PRIORITY_LABEL: Record<CalloutPriority, string> = { low: "Low", medium: "Medium", high: "High", emergency: "Emergency" };
const STATUS_LABEL: Record<CalloutStatus, string> = { open: "Open", in_progress: "In Progress", resolved: "Resolved" };
const CALLOUT_PRIORITIES: CalloutPriority[] = ["low", "medium", "high", "emergency"];
const CALLOUT_STATUSES: CalloutStatus[] = ["open", "in_progress", "resolved"];

function emptyCalloutForm(): any {
  return {
    callDate: new Date().toISOString().slice(0, 10),
    callType: "",
    priority: "medium",
    status: "open",
    engineer: "",
    description: "",
    resolvedDate: "",
    notes: "",
    siteAddress: "",
    parkingAvailable: "unknown",
    siteContactName: "",
    siteContactPhone: "",
  };
}

function CallOutsSection({ ownerType, ownerId }: { ownerType: "customer" | "company"; ownerId: number }) {
  const [rows, setRows] = useState<CalloutRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState<any>(emptyCalloutForm());
  const [saving, setSaving] = useState(false);
  const [attachOpen, setAttachOpen] = useState<CalloutRow | null>(null);
  const [counts, setCounts] = useState<Record<number, number>>({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts?ownerType=${ownerType}&ownerId=${ownerId}`, { credentials: "include" });
      if (r.ok) {
        const data: CalloutRow[] = await r.json();
        setRows(data);
        const all = await Promise.all(data.map(async d => {
          const ar = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/${d.id}/attachments`, { credentials: "include" });
          return [d.id, ar.ok ? (await ar.json()).length : 0] as [number, number];
        }));
        setCounts(Object.fromEntries(all));
      }
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [ownerType, ownerId]);

  const startNew = () => { setForm(emptyCalloutForm()); setEditingId("new"); };
  const startEdit = (row: CalloutRow) => {
    setForm({
      callDate: row.callDate ? row.callDate.slice(0, 10) : "",
      callType: row.callType || "",
      priority: row.priority,
      status: row.status,
      engineer: row.engineer || "",
      description: row.description || "",
      resolvedDate: row.resolvedDate ? row.resolvedDate.slice(0, 10) : "",
      notes: row.notes || "",
      siteAddress: row.siteAddress || "",
      parkingAvailable: row.parkingAvailable || "unknown",
      siteContactName: row.siteContactName || "",
      siteContactPhone: row.siteContactPhone || "",
    });
    setEditingId(row.id);
  };
  const cancel = () => { setEditingId(null); };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ownerType, ownerId,
        callDate: form.callDate,
        callType: form.callType,
        priority: form.priority,
        status: form.status,
        engineer: form.engineer || null,
        description: form.description || null,
        resolvedDate: form.resolvedDate || null,
        notes: form.notes || null,
        siteAddress: form.siteAddress || null,
        parkingAvailable: form.parkingAvailable || null,
        siteContactName: form.siteContactName || null,
        siteContactPhone: form.siteContactPhone || null,
      };
      const url = editingId === "new"
        ? `${import.meta.env.BASE_URL}api/admin/callouts`
        : `${import.meta.env.BASE_URL}api/admin/callouts/${editingId}`;
      const method = editingId === "new" ? "POST" : "PUT";
      const r = await fetch(url, { method, headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });
      if (r.ok) { setEditingId(null); load(); }
    } catch {}
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!confirm("Delete this call-out and its attachments?")) return;
    const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/${id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) load();
  };

  const summary = {
    open: rows.filter(r => r.status === "open").length,
    inProgress: rows.filter(r => r.status === "in_progress").length,
    resolved: rows.filter(r => r.status === "resolved").length,
    emergency: rows.filter(r => r.priority === "emergency" && r.status !== "resolved").length,
  };

  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="p-5 border-b border-border bg-gradient-to-r from-rose-50 via-white to-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-rose-100"><AlertTriangle className="w-4 h-4 text-rose-600" /></div>
            <div>
              <h2 className="font-bold text-foreground">Call Outs Schedules</h2>
              <p className="text-xs text-muted-foreground">Reactive visits & emergency call-outs</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs px-2.5 py-1 rounded-full border bg-red-50 text-red-700 border-red-200 font-semibold">Open: {summary.open}</span>
            <span className="text-xs px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200 font-semibold">In Progress: {summary.inProgress}</span>
            <span className="text-xs px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold">Resolved: {summary.resolved}</span>
            {summary.emergency > 0 && (
              <span className="text-xs px-2.5 py-1 rounded-full border bg-red-100 text-red-800 border-red-300 font-bold">⚠ {summary.emergency} Emergency</span>
            )}
            <button onClick={startNew} className="ml-1 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary/90 transition-colors flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> New Call Out
            </button>
          </div>
        </div>
      </div>

      {editingId !== null && (
        <div className="p-5 bg-slate-50 border-b border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Call Date</div>
              <input type="date" value={form.callDate} onChange={e => setForm({ ...form, callDate: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Type / Issue</div>
              <input type="text" value={form.callType} onChange={e => setForm({ ...form, callType: e.target.value })} placeholder="e.g. No heating, Leak, Fault" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Priority</div>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Status</div>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary">
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Engineer</div>
              <input type="text" value={form.engineer} onChange={e => setForm({ ...form, engineer: e.target.value })} placeholder="Engineer name" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Resolved Date</div>
              <input type="date" value={form.resolvedDate} onChange={e => setForm({ ...form, resolvedDate: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div className="md:col-span-2 lg:col-span-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Site Address</div>
              <textarea value={form.siteAddress} onChange={e => setForm({ ...form, siteAddress: e.target.value })} rows={2} placeholder="Full site address where work is to be carried out" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Parking Available</div>
              <select value={form.parkingAvailable} onChange={e => setForm({ ...form, parkingAvailable: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary">
                <option value="unknown">Unknown</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Site Contact Name</div>
              <input type="text" value={form.siteContactName} onChange={e => setForm({ ...form, siteContactName: e.target.value })} placeholder="On-site contact" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div className="md:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Site Contact Phone</div>
              <input type="tel" value={form.siteContactPhone} onChange={e => setForm({ ...form, siteContactPhone: e.target.value })} placeholder="e.g. 07700 900000" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div className="md:col-span-2 lg:col-span-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Description</div>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="What was reported?" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div className="md:col-span-2 lg:col-span-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Internal Notes</div>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Resolution notes, parts used, etc." className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={save} disabled={saving || !form.callDate || !form.callType} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-60 transition-colors flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
            </button>
            <button onClick={cancel} className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-100 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-12 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="p-12 text-center text-sm text-muted-foreground">No call-outs logged yet. Click <strong>New Call Out</strong> to add one.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-border">
              <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3 text-left font-semibold">Type</th>
                <th className="px-4 py-3 text-left font-semibold">Priority</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Engineer</th>
                <th className="px-4 py-3 text-left font-semibold">Resolved</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-3 whitespace-nowrap text-foreground">{fmtDate(r.callDate)}</td>
                  <td className="px-4 py-3 text-foreground font-medium">
                    {r.callType}
                    {r.description && <div className="text-xs text-muted-foreground font-normal mt-0.5 line-clamp-1">{r.description}</div>}
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${PRIORITY_TONES[r.priority]}`}>{PRIORITY_LABEL[r.priority]}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${STATUS_TONES[r.status]}`}>{STATUS_LABEL[r.status]}</span></td>
                  <td className="px-4 py-3 text-foreground">{r.engineer || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.resolvedDate ? fmtDate(r.resolvedDate) : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setAttachOpen(r)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-sky-700 hover:bg-sky-50 transition-colors flex items-center gap-1.5 relative">
                        <Paperclip className="w-3.5 h-3.5" /> Files
                        {counts[r.id] > 0 && <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold">{counts[r.id]}</span>}
                      </button>
                      <button onClick={() => startEdit(r)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-slate-100 transition-colors">Edit</button>
                      <button onClick={() => remove(r.id)} className="px-2 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {attachOpen && (
        <CallOutAttachmentsModal callOut={attachOpen} onClose={() => { setAttachOpen(null); load(); }} />
      )}
    </div>
  );
}

function CallOutAttachmentsModal({ callOut, onClose }: { callOut: CalloutRow; onClose: () => void }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/${callOut.id}/attachments`, { credentials: "include" });
      if (r.ok) setItems(await r.json());
    } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [callOut.id]);

  const upload = async () => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { alert("File too large (max 15MB)"); return; }
    setUploading(true);
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => {
          const result = String(fr.result || "");
          const idx = result.indexOf(",");
          resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(file);
      });
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/${callOut.id}/attachments`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ fileName: file.name, mimeType: file.type || "application/octet-stream", fileSize: file.size, notes: notes || null, data }),
      });
      if (r.ok) { setFile(null); setNotes(""); load(); }
      else { const e = await r.json().catch(() => ({})); alert(e.error || "Upload failed"); }
    } catch (e: any) { alert(e?.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (id: number) => {
    if (!confirm("Delete this attachment?")) return;
    const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/attachments/${id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h3 className="font-bold text-foreground">Attachments</h3>
            <p className="text-xs text-muted-foreground">{callOut.callType} · {fmtDate(callOut.callDate)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 border-b border-border bg-slate-50">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">File (max 15MB)</div>
              <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="w-full text-xs" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Notes</div>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm" />
            </div>
            <button onClick={upload} disabled={!file || uploading} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-60 transition-colors flex items-center gap-2">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
            </button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">No attachments yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map(it => (
                <li key={it.id} className="py-3 flex items-center gap-3">
                  <FolderOpen className="w-5 h-5 text-sky-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{it.fileName}</div>
                    <div className="text-xs text-muted-foreground">{(it.fileSize / 1024).toFixed(1)} KB · {new Date(it.uploadedAt).toLocaleString("en-GB")}{it.notes ? ` · ${it.notes}` : ""}</div>
                  </div>
                  <a href={`${import.meta.env.BASE_URL}api/admin/callouts/attachments/${it.id}/download`} className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-sky-700 hover:bg-sky-50 flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Download</a>
                  <button onClick={() => remove(it.id)} className="px-2 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function CompanyEditor({ company, onClose, onSaved, onDeleted }: { company: CompanyRow; onClose: () => void; onSaved: (c: CompanyRow) => void; onDeleted: (id: number) => void }) {
  const [form, setForm] = useState<CompanyRow>(company);
  const [saving, setSaving] = useState(false);
  const [accInput, setAccInput] = useState("");
  const [error, setError] = useState("");

  const update = (k: keyof CompanyRow, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setError("");
    if (!form.tradingName.trim()) { setError("Trading name is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/companies/${company.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || "Save failed");
        return;
      }
      const updated = await res.json();
      onSaved(updated);
      onClose();
    } catch { setError("Network error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete company "${form.tradingName}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/companies/${company.id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { onDeleted(company.id); onClose(); }
    } catch {}
  };

  const accreditations = form.accreditations || [];
  const addAcc = () => {
    const v = accInput.trim();
    if (!v || accreditations.includes(v)) { setAccInput(""); return; }
    update("accreditations", [...accreditations, v]);
    setAccInput("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onClose} className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="w-4 h-4" /> Back to companies
        </button>
        <div className="flex items-center gap-2">
          <button onClick={handleDelete} className="px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-60 transition-colors flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save changes
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-foreground">Company Information</h2>
              <p className="text-xs text-muted-foreground">Registered business details</p>
            </div>
          </div>

          <div className="space-y-4">
            <FieldInput label="Trading Name" value={form.tradingName} onChange={v => update("tradingName", v)} placeholder="VertexAir Climate Systems" />
            <FieldInput label="Operating Company" value={form.operatingCompany || ""} onChange={v => update("operatingCompany", v)} placeholder="Elecwest Electrical Contractors Ltd" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldInput label="Company Number" value={form.companyNumber || ""} onChange={v => update("companyNumber", v)} placeholder="07412938" />
              <FieldInput label="VAT Number" value={form.vatNumber || ""} onChange={v => update("vatNumber", v)} placeholder="GB 102 8473 91" />
            </div>
            <FieldTextarea label="Registered Address" value={form.address || ""} onChange={v => update("address", v)} placeholder="86–90 Paul Street, London, EC2A 4NE, United Kingdom" rows={2} />
            <FieldInput label="Primary Contact Name" value={form.contactName || ""} onChange={v => update("contactName", v)} placeholder="John Doe" />
            <FieldTextarea label="Notes" value={form.notes || ""} onChange={v => update("notes", v)} placeholder="Internal notes about this company" rows={3} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <PhoneIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-foreground">Contact & Account</h2>
              <p className="text-xs text-muted-foreground">Channels & login credentials</p>
            </div>
          </div>

          <div className="space-y-4">
            <FieldInput label="Phone" value={form.phone || ""} onChange={v => update("phone", v)} type="tel" placeholder="020 3872 0000" />
            <FieldInput label="Email" value={form.email || ""} onChange={v => update("email", v)} type="email" placeholder="hello@example.com" />
            <FieldInput label="Website" value={form.website || ""} onChange={v => update("website", v)} placeholder="example.com" />

            <div className="pt-4 border-t border-slate-100">
              {form.email ? (
                <LoginCredentials ownerType="company" ownerId={company.id} email={form.email} />
              ) : (
                <div className="flex items-start gap-2 text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                  <span>Add an email above (then save) to enable a portal login for this company.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
            <Award className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-foreground">Accreditations & Certifications</h2>
            <p className="text-xs text-muted-foreground">Industry credentials this company holds</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3 min-h-[2rem]">
          {accreditations.length === 0 && <span className="text-sm text-muted-foreground">No accreditations added yet.</span>}
          {accreditations.map(a => (
            <span key={a} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-700">
              {a}
              <button onClick={() => update("accreditations", accreditations.filter(x => x !== a))} className="text-slate-400 hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={accInput}
            onChange={(e) => setAccInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAcc(); } }}
            placeholder="Add accreditation (e.g. F-Gas Certified)"
            className="flex-1 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          <button onClick={addAcc} className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-foreground hover:bg-slate-200 transition-colors flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <PpmSection ownerType="company" ownerId={company.id} />
      <CallOutsSection ownerType="company" ownerId={company.id} />
    </div>
  );
}

function CompanyView() {
  const [list, setList] = useState<CompanyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<CompanyRow | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/companies`, { credentials: "include" });
      if (res.ok) setList(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchList(); }, []);

  const handleCreate = async () => {
    const name = window.prompt("Company trading name?");
    if (!name || !name.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/companies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tradingName: name.trim(), accreditations: [] }),
      });
      if (res.ok) {
        const created = await res.json();
        setList(prev => [created, ...prev]);
        setSelected(created);
      }
    } catch {}
    finally { setCreating(false); }
  };

  const filtered = list.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return c.tradingName.toLowerCase().includes(q)
      || (c.operatingCompany || "").toLowerCase().includes(q)
      || (c.email || "").toLowerCase().includes(q)
      || (c.phone || "").toLowerCase().includes(q)
      || (c.address || "").toLowerCase().includes(q);
  });

  if (selected) {
    return (
      <CompanyEditor
        company={selected}
        onClose={() => setSelected(null)}
        onSaved={(c) => setList(prev => prev.map(x => x.id === c.id ? c : x))}
        onDeleted={(id) => setList(prev => prev.filter(x => x.id !== id))}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Companies" value={list.length} icon={Building2} color="bg-primary/10 text-primary" />
        <StatCard label="With Email" value={list.filter(c => c.email).length} icon={MailIcon} color="bg-emerald-50 text-emerald-600" />
        <StatCard label="With Phone" value={list.filter(c => c.phone).length} icon={PhoneIcon} color="bg-amber-50 text-amber-600" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-bold text-foreground">Commercial Companies</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              className="px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            <button onClick={handleCreate} disabled={creating} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-60 transition-colors flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Company
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-muted-foreground">{list.length === 0 ? "No companies yet. Click \"Add Company\" to create one." : "No companies match your search."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Trading Name</th>
                  <th className="px-4 py-3 font-semibold">Contact</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">Address</th>
                  <th className="px-4 py-3 font-semibold w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} onClick={() => setSelected(c)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Building2 className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">{c.tradingName}</div>
                          {c.operatingCompany && <div className="text-xs text-muted-foreground">{c.operatingCompany}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{c.contactName || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3">{c.email || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3">{c.phone || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 max-w-[16rem]">
                      {c.address ? <span className="text-foreground/80 line-clamp-2" title={c.address}>{c.address}</span> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <ChevronRight className="w-4 h-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ClientEditor({ client, onClose, onSaved, onDeleted }: { client: CustomerRow; onClose: () => void; onSaved: (c: CustomerRow) => void; onDeleted: (id: number) => void }) {
  const [form, setForm] = useState({
    name: client.name,
    email: client.email,
    phone: client.phone || "",
    address: client.address || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    setError("");
    if (!form.name.trim() || !form.email.trim()) { setError("Name and email are required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/customers/${client.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || "Save failed");
        return;
      }
      const updated = await res.json();
      onSaved({ ...client, ...updated });
      onClose();
    } catch { setError("Network error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete client "${form.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/customers/${client.id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { onDeleted(client.id); onClose(); }
    } catch {}
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onClose} className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="w-4 h-4" /> Back to clients
        </button>
        <div className="flex items-center gap-2">
          <button onClick={handleDelete} className="px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-60 transition-colors flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save changes
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-foreground">Client Information</h2>
              <p className="text-xs text-muted-foreground">Personal & contact details</p>
            </div>
          </div>

          <div className="space-y-4">
            <FieldInput label="Full Name" value={form.name} onChange={v => setForm({ ...form, name: v })} placeholder="John Smith" />
            <FieldInput label="Email" value={form.email} onChange={v => setForm({ ...form, email: v })} type="email" placeholder="client@email.com" />
            <FieldInput label="Phone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} type="tel" placeholder="020 8087 0906" />
            <FieldTextarea label="Address" value={form.address} onChange={v => setForm({ ...form, address: v })} placeholder="86–90 Paul Street, London, EC2A 4NE" rows={2} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-foreground">Account</h2>
              <p className="text-xs text-muted-foreground">Activity & login credentials</p>
            </div>
          </div>

          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">ID</div>
                <div className="text-foreground font-medium">#{client.id}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Joined</div>
                <div className="text-foreground font-medium">
                  {new Date(client.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Requests</div>
                <div>
                  <span className={`inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full text-xs font-semibold ${client.submissionCount > 0 ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500"}`}>
                    {client.submissionCount}
                  </span>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <LoginCredentials ownerType="customer" ownerId={client.id} email={form.email} />
            </div>
          </div>
        </div>
      </div>

      <PpmSection ownerType="customer" ownerId={client.id} />
      <CallOutsSection ownerType="customer" ownerId={client.id} />
    </div>
  );
}

function LoginCredentials({ ownerType, ownerId, email }: { ownerType: "customer" | "company"; ownerId: number; email: string }) {
  const endpoint = ownerType === "customer" ? "customers" : "companies";
  const customerId = ownerId;
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [emailCopied, setEmailCopied] = useState(false);
  const [pwCopied, setPwCopied] = useState(false);

  const generate = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let out = "";
    for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setPw(out);
    setShowPw(true);
    setMsg(null);
  };

  const save = async () => {
    if (pw.length < 6) { setMsg({ type: "err", text: "Password must be at least 6 characters" }); return; }
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/${endpoint}/${customerId}/password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: pw }),
      });
      if (res.ok) {
        setMsg({ type: "ok", text: "Password updated. Share these credentials with the client." });
      } else {
        const d = await res.json().catch(() => ({}));
        setMsg({ type: "err", text: d.error || "Failed to update password" });
      }
    } catch { setMsg({ type: "err", text: "Network error" }); }
    finally { setSaving(false); }
  };

  const copy = async (text: string, which: "email" | "pw") => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === "email") { setEmailCopied(true); setTimeout(() => setEmailCopied(false), 1500); }
      else { setPwCopied(true); setTimeout(() => setPwCopied(false), 1500); }
    } catch {}
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Lock className="w-3.5 h-3.5 text-amber-600" />
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Login Credentials</div>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Email</div>
        <div className="flex items-center gap-1.5">
          <input readOnly value={email} className="flex-1 min-w-0 px-2 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-xs font-mono text-foreground" />
          <button onClick={() => copy(email, "email")} className="shrink-0 p-1.5 rounded-md border border-slate-200 hover:border-primary/40 hover:bg-primary/5 text-foreground" title="Copy email">
            {emailCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Password</div>
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1 min-w-0">
            <input
              type={showPw ? "text" : "password"}
              value={pw}
              onChange={e => setPw(e.target.value)}
              placeholder="Set new password"
              className="w-full pl-2 pr-7 py-1.5 rounded-md border border-slate-200 bg-white text-xs font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-foreground" aria-label={showPw ? "Hide" : "Show"}>
              {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
          {pw && (
            <button onClick={() => copy(pw, "pw")} className="shrink-0 p-1.5 rounded-md border border-slate-200 hover:border-primary/40 hover:bg-primary/5 text-foreground" title="Copy password">
              {pwCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <button onClick={generate} className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Generate
          </button>
          <span className="text-slate-300">·</span>
          <button onClick={save} disabled={saving || !pw} className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
          </button>
        </div>
      </div>

      {msg && (
        <div className={`text-[11px] px-2 py-1.5 rounded-md border ${msg.type === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      <p className="text-[10px] leading-snug text-muted-foreground">
        Existing passwords are one-way encrypted and can't be displayed. Set a new one to share with the client.
      </p>
    </div>
  );
}

function ClientsView() {
  const [clients, setClients] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<CustomerRow | null>(null);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/customers`, { credentials: "include" });
      if (res.ok) setClients(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchClients(); }, []);

  const filtered = clients.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || (c.phone || "").toLowerCase().includes(q) || (c.address || "").toLowerCase().includes(q);
  });

  const totalRequests = clients.reduce((sum, c) => sum + (c.submissionCount || 0), 0);
  const newThisMonth = clients.filter(c => {
    const d = new Date(c.createdAt);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  if (selected) {
    return (
      <ClientEditor
        client={selected}
        onClose={() => setSelected(null)}
        onSaved={(c) => setClients(prev => prev.map(x => x.id === c.id ? c : x))}
        onDeleted={(id) => setClients(prev => prev.filter(x => x.id !== id))}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Clients" value={clients.length} icon={Users} color="bg-primary/10 text-primary" />
        <StatCard label="New This Month" value={newThisMonth} icon={Plus} color="bg-emerald-50 text-emerald-600" />
        <StatCard label="Total Requests" value={totalRequests} icon={FileText} color="bg-amber-50 text-amber-600" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-bold text-foreground">Registered Clients</h2>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email or phone..."
            className="px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm w-full sm:w-72 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-muted-foreground">{clients.length === 0 ? "No registered clients yet." : "No clients match your search."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">Address</th>
                  <th className="px-4 py-3 font-semibold text-center">Requests</th>
                  <th className="px-4 py-3 font-semibold">Joined</th>
                  <th className="px-4 py-3 font-semibold w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} onClick={() => setSelected(c)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                          {c.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                        <span className="font-medium text-foreground">{c.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-primary">{c.email}</td>
                    <td className="px-4 py-3">
                      {c.phone || <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3 max-w-[16rem]">
                      {c.address ? (
                        <span className="text-foreground/80 line-clamp-2" title={c.address}>{c.address}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full text-xs font-semibold ${c.submissionCount > 0 ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500"}`}>
                        {c.submissionCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(c.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <ChevronRight className="w-4 h-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PpmDashboard({ onOpen }: { onOpen: () => void }) {
  const [rows, setRows] = useState<PpmOwnerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date>(new Date());

  const load = async () => {
    setLoading(true);
    try {
      const [customersRes, companiesRes] = await Promise.all([
        fetch(`${import.meta.env.BASE_URL}api/admin/customers`, { credentials: "include" }),
        fetch(`${import.meta.env.BASE_URL}api/admin/companies`, { credentials: "include" }),
      ]);
      const customers: CustomerRow[] = customersRes.ok ? await customersRes.json() : [];
      const companies: CompanyRow[] = companiesRes.ok ? await companiesRes.json() : [];
      const ppmFetches = await Promise.all([
        ...customers.map(c => fetch(`${import.meta.env.BASE_URL}api/admin/ppm?ownerType=customer&ownerId=${c.id}`, { credentials: "include" }).then(r => r.ok ? r.json() : [])),
        ...companies.map(c => fetch(`${import.meta.env.BASE_URL}api/admin/ppm?ownerType=company&ownerId=${c.id}`, { credentials: "include" }).then(r => r.ok ? r.json() : [])),
      ]);
      const all: PpmOwnerRow[] = [];
      customers.forEach((c, i) => all.push({
        ownerType: "customer", ownerId: c.id, name: c.name, subtitle: "Domestic",
        schedules: new Map((ppmFetches[i] || []).map((r: PpmRow) => [r.serviceType, r])),
      }));
      companies.forEach((c, i) => all.push({
        ownerType: "company", ownerId: c.id, name: c.tradingName, subtitle: c.operatingCompany || "Commercial",
        schedules: new Map((ppmFetches[customers.length + i] || []).map((r: PpmRow) => [r.serviceType, r])),
      }));
      setRows(all);
      setRefreshedAt(new Date());
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const stats = (() => {
    const byService = PPM_SERVICES.map(s => {
      let overdue = 0, due = 0, missing = 0, ok = 0;
      rows.forEach(r => {
        const label = ppmStatus(r.schedules.get(s.key)).label;
        if (label === "Overdue") overdue++;
        else if (label === "Due") due++;
        else if (label === "Missing") missing++;
        else ok++;
      });
      return { key: s.key, label: s.label, overdue, due, missing, ok };
    });
    const overdueTotal = byService.reduce((s, x) => s + x.overdue, 0);
    const dueTotal = byService.reduce((s, x) => s + x.due, 0);
    const missingTotal = byService.reduce((s, x) => s + x.missing, 0);
    const ownersWithOverdue = rows.filter(r => PPM_SERVICES.some(s => ppmStatus(r.schedules.get(s.key)).label === "Overdue")).length;
    const topSites = rows.map(r => ({
      name: r.name,
      subtitle: r.subtitle,
      overdue: PPM_SERVICES.filter(s => ppmStatus(r.schedules.get(s.key)).label === "Overdue").length,
      due: PPM_SERVICES.filter(s => ppmStatus(r.schedules.get(s.key)).label === "Due").length,
      missing: PPM_SERVICES.filter(s => ppmStatus(r.schedules.get(s.key)).label === "Missing").length,
    })).filter(s => s.overdue > 0 || s.due > 0 || s.missing > 0)
       .sort((a, b) => b.overdue - a.overdue || b.due - a.due)
       .slice(0, 5);
    return { byService, overdueTotal, dueTotal, missingTotal, ownersWithOverdue, topSites };
  })();

  const chartData = stats.byService.map(s => ({ name: s.label, Overdue: s.overdue, Missing: s.missing }));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 border-b border-slate-100">
        <div className="lg:col-span-3 p-5 bg-amber-50/40 border-b lg:border-b-0 lg:border-r border-slate-100 flex flex-col justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-amber-700">Last Refreshed</div>
            <div className="text-sm font-semibold text-foreground mt-2">
              {refreshedAt.toLocaleDateString("en-GB")} {refreshedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
          </div>
          <button onClick={load} disabled={loading} className="mt-3 self-start inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <div className="lg:col-span-3 p-5 border-b lg:border-b-0 lg:border-r border-slate-100 flex flex-col items-center justify-center text-center">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Overdue PPMs</div>
          <div className="text-5xl font-extrabold text-red-600 mt-2 leading-none">{stats.overdueTotal}</div>
          <div className="text-xs text-muted-foreground mt-2">across {stats.ownersWithOverdue} site{stats.ownersWithOverdue === 1 ? "" : "s"}</div>
        </div>
        <div className="lg:col-span-6 p-5">
          <div className="text-center text-sm font-bold text-foreground mb-3">Overdue PPMs by Type</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-slate-100">
                  <th className="px-2 py-2 font-semibold">PPM Type</th>
                  <th className="px-2 py-2 font-semibold text-right">Overdue</th>
                  <th className="px-2 py-2 font-semibold text-right">Due Soon</th>
                  <th className="px-2 py-2 font-semibold text-right">Missing</th>
                  <th className="px-2 py-2 font-semibold text-right">In Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.byService.map(s => (
                  <tr key={s.key} className="border-b border-slate-50 last:border-0">
                    <td className="px-2 py-1.5 text-foreground">{s.label}</td>
                    <td className="px-2 py-1.5 text-right font-bold text-red-600">{s.overdue || <span className="text-slate-300 font-normal">—</span>}</td>
                    <td className="px-2 py-1.5 text-right font-semibold text-amber-600">{s.due || <span className="text-slate-300 font-normal">—</span>}</td>
                    <td className="px-2 py-1.5 text-right font-semibold text-slate-500">{s.missing || <span className="text-slate-300 font-normal">—</span>}</td>
                    <td className="px-2 py-1.5 text-right font-semibold text-emerald-600">{s.ok || <span className="text-slate-300 font-normal">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {stats.topSites.length > 0 && (
        <div className="p-5 border-b border-slate-100">
          <div className="text-center text-sm font-bold text-foreground mb-3">Top Sites By Overdue PPMs</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-slate-100">
                  <th className="px-2 py-2 font-semibold">Site Name</th>
                  <th className="px-2 py-2 font-semibold">Type</th>
                  <th className="px-2 py-2 font-semibold text-right">Overdue</th>
                  <th className="px-2 py-2 font-semibold text-right">Due Soon</th>
                  <th className="px-2 py-2 font-semibold text-right">Missing</th>
                </tr>
              </thead>
              <tbody>
                {stats.topSites.map((s, i) => (
                  <tr key={i} className="border-b border-slate-50 last:border-0">
                    <td className="px-2 py-1.5 font-semibold text-foreground">{s.name}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{s.subtitle}</td>
                    <td className="px-2 py-1.5 text-right font-bold text-red-600">{s.overdue || <span className="text-slate-300 font-normal">—</span>}</td>
                    <td className="px-2 py-1.5 text-right font-semibold text-amber-600">{s.due || <span className="text-slate-300 font-normal">—</span>}</td>
                    <td className="px-2 py-1.5 text-right font-semibold text-slate-500">{s.missing || <span className="text-slate-300 font-normal">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold text-foreground flex-1 text-center">Overdue & Missing PPMs by Category</div>
          <button onClick={onOpen} className="text-xs font-semibold text-primary hover:underline">View all →</button>
        </div>
        {loading && rows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Overdue" fill="#dc2626" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Missing" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

interface AdminMe { id: number; name: string; email: string; phone?: string | null; avatar?: string | null; createdAt?: string }
interface AdminTeamMember { id: number; name: string; email: string; phone: string | null; avatar: string | null; createdAt: string }
interface AdminActivityRow { id: number; adminId: number | null; adminName: string | null; action: string; entityType: string | null; entityId: string | null; description: string | null; createdAt: string }

function ProfileView({ me, onUpdated }: { me: AdminMe | null; onUpdated: (m: AdminMe) => void }) {
  const [tab, setTab] = useState<"profile" | "activity">("profile");
  const [name, setName] = useState(me?.name || "");
  const [email, setEmail] = useState(me?.email || "");
  const [phone, setPhone] = useState(me?.phone || "");
  const [avatar, setAvatar] = useState<string | null>(me?.avatar || null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const [activity, setActivity] = useState<AdminActivityRow[]>([]);
  const [team, setTeam] = useState<AdminTeamMember[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState<"mine" | "all">("mine");

  useEffect(() => {
    setName(me?.name || ""); setEmail(me?.email || ""); setPhone(me?.phone || ""); setAvatar(me?.avatar || null);
  }, [me?.id]);

  const loadActivity = async () => {
    setActivityLoading(true);
    try {
      const url = activityFilter === "mine" && me ? `${import.meta.env.BASE_URL}api/admin/activity?adminId=${me.id}` : `${import.meta.env.BASE_URL}api/admin/activity`;
      const [aRes, tRes] = await Promise.all([
        fetch(url, { credentials: "include" }),
        fetch(`${import.meta.env.BASE_URL}api/admin/team`, { credentials: "include" }),
      ]);
      if (aRes.ok) setActivity(await aRes.json());
      if (tRes.ok) setTeam(await tRes.json());
    } catch {}
    finally { setActivityLoading(false); }
  };

  useEffect(() => { if (tab === "activity") loadActivity(); }, [tab, activityFilter, me?.id]);

  const handleAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setMsg({ type: "err", text: "Image must be under 2MB" }); return; }
    const reader = new FileReader();
    reader.onload = () => setAvatar(String(reader.result));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/me`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone, avatar }),
      });
      const data = await res.json();
      if (!res.ok) { setMsg({ type: "err", text: data.error || "Failed to save" }); return; }
      onUpdated(data);
      setMsg({ type: "ok", text: "Profile updated" });
    } catch { setMsg({ type: "err", text: "Failed to save" }); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    setPwSaving(true); setPwMsg(null);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/me/password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: pwCurrent, newPassword: pwNew }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setPwMsg({ type: "err", text: data.error || "Failed" }); return; }
      setPwMsg({ type: "ok", text: "Password updated" });
      setPwCurrent(""); setPwNew("");
    } catch { setPwMsg({ type: "err", text: "Failed" }); }
    finally { setPwSaving(false); }
  };

  const initials = (name || "A").trim().split(/\s+/).map(p => p[0]).slice(0, 2).join("").toUpperCase();

  const actionLabel = (a: string) => a.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const formatTime = (s: string) => new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl w-fit">
        <button onClick={() => setTab("profile")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === "profile" ? "bg-white text-foreground shadow-sm" : "text-slate-600"}`}>Profile</button>
        <button onClick={() => setTab("activity")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === "activity" ? "bg-white text-foreground shadow-sm" : "text-slate-600"}`}>Activity Log</button>
      </div>

      {tab === "profile" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
              <div className="relative inline-block">
                {avatar ? (
                  <img src={avatar} alt="" className="w-28 h-28 rounded-full object-cover mx-auto shadow-md" />
                ) : (
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary to-sky-600 text-white flex items-center justify-center text-3xl font-bold mx-auto shadow-md">{initials}</div>
                )}
                <label className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center cursor-pointer shadow hover:bg-primary/90" title="Change photo">
                  <Camera className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleAvatarPick} />
                </label>
              </div>
              <div className="mt-4 text-lg font-bold text-foreground">{name || "—"}</div>
              <div className="text-sm text-muted-foreground">{email}</div>
              {avatar && (
                <button onClick={() => setAvatar(null)} className="mt-3 text-xs text-muted-foreground hover:text-red-600">Remove photo</button>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="text-base font-bold text-foreground mb-4">Personal information</h3>
              {msg && (
                <div className={`text-sm px-3 py-2 rounded-lg mb-4 ${msg.type === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{msg.text}</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Full name</label>
                  <input value={name} onChange={e => setName(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Phone</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. +44 20 7946 0000" className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
                </button>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="text-base font-bold text-foreground mb-1">Change password</h3>
              <p className="text-xs text-muted-foreground mb-4">Use at least 8 characters.</p>
              {pwMsg && (
                <div className={`text-sm px-3 py-2 rounded-lg mb-4 ${pwMsg.type === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{pwMsg.text}</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Current password</label>
                  <input type="password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">New password</label>
                  <input type="password" value={pwNew} onChange={e => setPwNew(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <button onClick={changePassword} disabled={pwSaving || !pwCurrent || pwNew.length < 8} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60">
                  {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Update password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "activity" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-base font-bold text-foreground">Activity Log</h3>
              <p className="text-xs text-muted-foreground">Track everything admins do across the platform.</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                <button onClick={() => setActivityFilter("mine")} className={`px-3 py-1.5 text-xs font-medium ${activityFilter === "mine" ? "bg-primary text-white" : "text-muted-foreground"}`}>My activity</button>
                <button onClick={() => setActivityFilter("all")} className={`px-3 py-1.5 text-xs font-medium ${activityFilter === "all" ? "bg-primary text-white" : "text-muted-foreground"}`}>All admins</button>
              </div>
              <button onClick={loadActivity} className="p-2 rounded-lg hover:bg-slate-100 text-muted-foreground" title="Refresh">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {activityFilter === "all" && team.length > 0 && (
            <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap gap-2 bg-slate-50/50">
              {team.map(t => {
                const tInit = t.name.trim().split(/\s+/).map(p => p[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <div key={t.id} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-3 py-1 text-xs">
                    {t.avatar ? <img src={t.avatar} alt="" className="w-6 h-6 rounded-full object-cover" /> : <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-sky-600 text-white flex items-center justify-center font-bold text-[10px]">{tInit}</div>}
                    <span className="font-medium text-foreground">{t.name}</span>
                  </div>
                );
              })}
            </div>
          )}

          {activityLoading ? (
            <div className="p-12 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : activity.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground text-sm">No activity recorded yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {activity.map(row => {
                const init = (row.adminName || "?").trim().split(/\s+/).map(p => p[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <li key={row.id} className="px-5 py-3 flex items-start gap-3 hover:bg-slate-50/50">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 text-white flex items-center justify-center font-bold text-[11px] flex-shrink-0">{init}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-foreground">{row.adminName || "Unknown"}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{actionLabel(row.action)}</span>
                        {row.entityType && <span className="text-xs text-muted-foreground">{row.entityType}{row.entityId ? ` #${row.entityId}` : ""}</span>}
                      </div>
                      {row.description && <div className="text-sm text-slate-600 mt-0.5">{row.description}</div>}
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">{formatTime(row.createdAt)}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function HomeView({ submissionCount, onNavigate }: { submissionCount: number; onNavigate: (v: SidebarView) => void }) {
  const cards: { key: SidebarView; title: string; subtitle: string; icon: any; gradient: string; iconBg: string }[] = [
    {
      key: "analytics",
      title: "Analytics",
      subtitle: "Visitor traffic, journeys, top pages and conversion events",
      icon: TrendingUp,
      gradient: "from-sky-500 to-blue-600",
      iconBg: "bg-white/20",
    },
    {
      key: "ppm",
      title: "PPM Schedules",
      subtitle: "Track maintenance status across all clients & companies",
      icon: ClipboardList,
      gradient: "from-emerald-500 to-teal-600",
      iconBg: "bg-white/20",
    },
    {
      key: "callouts",
      title: "Schedules",
      subtitle: "All calendar schedules tagged by type",
      icon: AlertTriangle,
      gradient: "from-rose-500 to-red-600",
      iconBg: "bg-white/20",
    },
    {
      key: "forms",
      title: "Forms",
      subtitle: "Callbacks, quote requests and contact submissions",
      icon: FileText,
      gradient: "from-amber-500 to-orange-600",
      iconBg: "bg-white/20",
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
        <p className="text-sm text-muted-foreground mt-1">Jump into the area you want to manage today.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              onClick={() => onNavigate(c.key)}
              className={`group relative overflow-hidden rounded-xl bg-gradient-to-br ${c.gradient} p-3 text-left text-white shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5`}
            >
              <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-white/10 group-hover:scale-110 transition-transform" />
              <div className="relative flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${c.iconBg} backdrop-blur flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold leading-tight truncate">{c.title}</div>
                  <div className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-white/90">
                    Open <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button onClick={() => onNavigate("clients")} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-primary/40 hover:shadow-sm transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Users className="w-5 h-5" /></div>
            <div>
              <div className="font-semibold text-foreground">Domestic Clients</div>
              <div className="text-xs text-muted-foreground">Manage individual customers</div>
            </div>
          </div>
        </button>
        <button onClick={() => onNavigate("company")} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-primary/40 hover:shadow-sm transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
            <div>
              <div className="font-semibold text-foreground">Commercial Companies</div>
              <div className="text-xs text-muted-foreground">Manage business accounts</div>
            </div>
          </div>
        </button>
        <button onClick={() => onNavigate("calendar")} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-primary/40 hover:shadow-sm transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><CalendarDays className="w-5 h-5" /></div>
            <div>
              <div className="font-semibold text-foreground">Calendar</div>
              <div className="text-xs text-muted-foreground">{submissionCount} submissions in inbox</div>
            </div>
          </div>
        </button>
      </div>

      <PpmDashboard onOpen={() => onNavigate("ppm")} />
    </div>
  );
}

interface PpmOwnerRow {
  ownerType: "customer" | "company";
  ownerId: number;
  name: string;
  subtitle: string;
  schedules: Map<PpmServiceType, PpmRow>;
}

function PpmOverview() {
  const [rows, setRows] = useState<PpmOwnerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "overdue" | "due" | "missing">("all");
  const [selected, setSelected] = useState<PpmOwnerRow | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [customersRes, companiesRes] = await Promise.all([
        fetch(`${import.meta.env.BASE_URL}api/admin/customers`, { credentials: "include" }),
        fetch(`${import.meta.env.BASE_URL}api/admin/companies`, { credentials: "include" }),
      ]);
      const customers: CustomerRow[] = customersRes.ok ? await customersRes.json() : [];
      const companies: CompanyRow[] = companiesRes.ok ? await companiesRes.json() : [];

      const ppmFetches = await Promise.all([
        ...customers.map(c => fetch(`${import.meta.env.BASE_URL}api/admin/ppm?ownerType=customer&ownerId=${c.id}`, { credentials: "include" }).then(r => r.ok ? r.json() : [])),
        ...companies.map(c => fetch(`${import.meta.env.BASE_URL}api/admin/ppm?ownerType=company&ownerId=${c.id}`, { credentials: "include" }).then(r => r.ok ? r.json() : [])),
      ]);

      const all: PpmOwnerRow[] = [];
      customers.forEach((c, i) => {
        const ppm: PpmRow[] = ppmFetches[i] || [];
        all.push({
          ownerType: "customer",
          ownerId: c.id,
          name: c.name,
          subtitle: "Domestic",
          schedules: new Map(ppm.map(r => [r.serviceType, r])),
        });
      });
      companies.forEach((c, i) => {
        const ppm: PpmRow[] = ppmFetches[customers.length + i] || [];
        all.push({
          ownerType: "company",
          ownerId: c.id,
          name: c.tradingName,
          subtitle: c.operatingCompany || "Commercial",
          schedules: new Map(ppm.map(r => [r.serviceType, r])),
        });
      });
      setRows(all);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const totals = (() => {
    const t = { overdue: 0, due: 0, ok: 0, missing: 0 };
    rows.forEach(r => {
      PPM_SERVICES.forEach(s => {
        const status = ppmStatus(r.schedules.get(s.key));
        if (status.label === "Overdue") t.overdue++;
        else if (status.label === "Due") t.due++;
        else if (status.label === "OK") t.ok++;
        else t.missing++;
      });
    });
    return t;
  })();

  const filtered = rows.filter(r => {
    const q = search.trim().toLowerCase();
    if (q && !r.name.toLowerCase().includes(q) && !r.subtitle.toLowerCase().includes(q)) return false;
    if (filter === "all") return true;
    return PPM_SERVICES.some(s => {
      const label = ppmStatus(r.schedules.get(s.key)).label.toLowerCase();
      return label === filter;
    });
  });

  if (selected) {
    return <PpmOwnerDetail owner={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Overdue" value={totals.overdue} icon={AlertTriangle} color="bg-red-50 text-red-600" />
        <StatCard label="Due Soon" value={totals.due} icon={Clock} color="bg-amber-50 text-amber-600" />
        <StatCard label="OK" value={totals.ok} icon={Check} color="bg-emerald-50 text-emerald-600" />
        <StatCard label="Missing" value={totals.missing} icon={X} color="bg-slate-100 text-slate-600" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-bold text-foreground">All PPM Schedules</h2>
            <p className="text-xs text-muted-foreground">Detailed breakdown across clients & companies</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
              {(["all", "overdue", "due", "missing"] as const).map(f => (
                <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-xs font-semibold transition-all capitalize ${filter === f ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>{f}</button>
              ))}
            </div>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search owner..."
              className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-muted-foreground">{rows.length === 0 ? "No clients or companies yet." : "No records match your filters."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold sticky left-0 bg-slate-50 z-10">Owner</th>
                  {PPM_SERVICES.map(s => (
                    <th key={s.key} className="px-3 py-3 font-semibold whitespace-nowrap">{s.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr
                    key={`${r.ownerType}-${r.ownerId}`}
                    onClick={() => setSelected(r)}
                    className="border-b border-slate-100 last:border-0 hover:bg-primary/5 transition-colors group cursor-pointer"
                    title={`View PPM schedule for ${r.name}`}
                  >
                    <td className="px-4 py-3 sticky left-0 bg-white group-hover:bg-primary/5 z-10 transition-colors">
                      <div className="flex items-center gap-2.5 min-w-[12rem]">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${r.ownerType === "company" ? "bg-primary/10 text-primary" : "bg-emerald-50 text-emerald-600"}`}>
                          {r.ownerType === "company" ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-foreground leading-tight group-hover:text-primary transition-colors">{r.name}</div>
                          <div className="text-xs text-muted-foreground">{r.subtitle}</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </td>
                    {PPM_SERVICES.map(s => {
                      const row = r.schedules.get(s.key);
                      const status = ppmStatus(row);
                      return (
                        <td key={s.key} className="px-3 py-3 align-top">
                          <div className={`inline-flex flex-col items-start gap-0.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold whitespace-nowrap ${status.tone}`}>
                            <span className="flex items-center gap-1">
                              {status.label === "Overdue" && <AlertTriangle className="w-3 h-3" />}
                              {status.label}
                            </span>
                            {row?.lastServiced && <span className="font-normal opacity-80">Last: {fmtDate(row.lastServiced)}</span>}
                            {row?.dueDate && <span className="font-normal opacity-80">Due: {fmtDate(row.dueDate)}</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

type CalloutWithOwner = CalloutRow & { ownerName: string; ownerSubtitle: string };

type CalloutAttachment = {
  id: number;
  fileName: string;
  mimeType: string;
  fileSize: number;
  notes: string | null;
  uploadedAt: string;
};

const isImage = (mt: string) => mt.startsWith("image/");
const isVideo = (mt: string) => mt.startsWith("video/");
const isMedia = (mt: string) => isImage(mt) || isVideo(mt);

type EditField = "callDate" | "callType" | "priority" | "status" | "engineer" | "resolvedDate" | "siteAddress" | "siteContactName" | "siteContactPhone" | "parkingAvailable" | "description" | "notes";

function CallOutDetailModal({ callOut: initialCallOut, onClose, onChanged }: { callOut: CalloutWithOwner; onClose: () => void; onChanged?: () => void }) {
  const [callOut, setCallOut] = useState<CalloutWithOwner>(initialCallOut);
  const [items, setItems] = useState<CalloutAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [lightbox, setLightbox] = useState<CalloutAttachment | null>(null);
  const [editing, setEditing] = useState<EditField | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const beginEdit = (field: EditField, currentValue: any) => {
    setDraft(currentValue == null ? "" : String(currentValue));
    setEditing(field);
  };
  const cancelEdit = () => { setEditing(null); setDraft(""); };
  const saveField = async (field: EditField) => {
    setSaving(true);
    try {
      const payload: any = {
        callDate: callOut.callDate,
        callType: callOut.callType,
        priority: callOut.priority,
        status: callOut.status,
        engineer: callOut.engineer,
        description: callOut.description,
        resolvedDate: callOut.resolvedDate,
        notes: callOut.notes,
        siteAddress: callOut.siteAddress,
        parkingAvailable: callOut.parkingAvailable,
        siteContactName: callOut.siteContactName,
        siteContactPhone: callOut.siteContactPhone,
      };
      payload[field] = draft === "" ? null : draft;
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/${callOut.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        const updated = await r.json();
        setCallOut({ ...callOut, ...updated });
        setEditing(null);
        onChanged?.();
      } else {
        const e = await r.json().catch(() => ({}));
        alert(e.error || "Failed to save");
      }
    } catch (e: any) { alert(e?.message || "Failed to save"); }
    finally { setSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/${callOut.id}/attachments`, { credentials: "include" });
      if (r.ok) setItems(await r.json());
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [callOut.id]);

  const upload = async () => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { alert("File too large (max 15MB)"); return; }
    setUploading(true);
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => {
          const result = String(fr.result || "");
          const idx = result.indexOf(",");
          resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(file);
      });
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/${callOut.id}/attachments`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ fileName: file.name, mimeType: file.type || "application/octet-stream", fileSize: file.size, notes: notes || null, data }),
      });
      if (r.ok) { setFile(null); setNotes(""); load(); onChanged?.(); }
      else { const e = await r.json().catch(() => ({})); alert(e.error || "Upload failed"); }
    } catch (e: any) { alert(e?.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (id: number) => {
    if (!confirm("Delete this attachment?")) return;
    const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/attachments/${id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) { load(); onChanged?.(); }
  };

  const mediaItems = items.filter(it => isMedia(it.mimeType));
  const reportItems = items.filter(it => !isMedia(it.mimeType));
  const cover = mediaItems.find(it => isImage(it.mimeType)) || mediaItems[0];
  const coverUrl = cover ? `${import.meta.env.BASE_URL}api/admin/callouts/attachments/${cover.id}/download` : null;

  const fmtFileSize = (b: number) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`;

  type FieldType = "text" | "date" | "select" | "textarea";
  const renderField = (
    label: string,
    field: EditField,
    type: FieldType,
    display: React.ReactNode,
    opts?: { options?: { value: string; label: string }[]; colSpan?: boolean }
  ) => {
    const isEditing = editing === field;
    const colCls = opts?.colSpan ? "md:col-span-2" : "";
    if (isEditing) {
      return (
        <div className={`flex flex-col ${colCls}`}>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{label}</div>
          <div className="flex items-start gap-1.5">
            {type === "select" ? (
              <select autoFocus value={draft} onChange={e => setDraft(e.target.value)} className="flex-1 px-2 py-1.5 rounded-md bg-white border border-primary/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                {opts!.options!.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : type === "textarea" ? (
              <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} rows={3} className="flex-1 px-2 py-1.5 rounded-md bg-white border border-primary/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            ) : (
              <input autoFocus type={type === "date" ? "date" : "text"} value={draft} onChange={e => setDraft(e.target.value)} className="flex-1 px-2 py-1.5 rounded-md bg-white border border-primary/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            )}
            <button onClick={() => saveField(field)} disabled={saving} className="p-1.5 rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-60 flex-shrink-0" title="Save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            </button>
            <button onClick={cancelEdit} disabled={saving} className="p-1.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 flex-shrink-0" title="Cancel">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className={`group/field flex flex-col ${colCls}`}>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5 flex items-center gap-1.5">
          {label}
          <button onClick={() => beginEdit(field, (callOut as any)[field] ?? "")} className="opacity-0 group-hover/field:opacity-100 transition-opacity p-0.5 rounded text-slate-400 hover:text-primary" title={`Edit ${label}`}>
            <Pencil className="w-3 h-3" />
          </button>
        </div>
        <div className="text-sm text-foreground">{display}</div>
      </div>
    );
  };

  const priorityOptions = CALLOUT_PRIORITIES.map(p => ({ value: p, label: PRIORITY_LABEL[p] }));
  const statusOptions = CALLOUT_STATUSES.map(s => ({ value: s, label: STATUS_LABEL[s] }));
  const parkingOptions = [
    { value: "", label: "Unknown" },
    { value: "yes", label: "Available on site" },
    { value: "no", label: "Not available" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-8 overflow-hidden flex flex-col">
        <div className="relative bg-slate-900 text-white">
          {coverUrl ? (
            isImage(cover!.mimeType) ? (
              <div className="relative h-72 md:h-96 bg-slate-900 cursor-zoom-in" onClick={() => setLightbox(cover!)}>
                <img src={coverUrl} alt={cover!.fileName} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
              </div>
            ) : (
              <div className="relative h-72 md:h-96 bg-black">
                <video src={coverUrl} controls className="w-full h-full object-contain" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent pointer-events-none" />
              </div>
            )
          ) : (
            <div className="h-44 md:h-56 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 flex items-center justify-center">
              <div className="text-center text-slate-400">
                <Camera className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <div className="text-xs uppercase tracking-wider">No photos uploaded yet</div>
              </div>
            </div>
          )}
          <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-lg bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white transition-colors z-10">
            <X className="w-5 h-5" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${PRIORITY_TONES[callOut.priority]}`}>{PRIORITY_LABEL[callOut.priority]}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_TONES[callOut.status]}`}>{STATUS_LABEL[callOut.status]}</span>
              <span className="text-xs text-slate-300">{fmtDate(callOut.callDate)}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold leading-tight drop-shadow">{callOut.callType}</h2>
            <p className="text-sm text-slate-200 mt-1">{callOut.ownerName} <span className="text-slate-400">· {callOut.ownerSubtitle}</span></p>
          </div>
        </div>

        <div className="p-5 md:p-6 grid md:grid-cols-2 gap-x-8 gap-y-3 border-b border-slate-100">
          <div className="flex flex-col">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5">Owner</div>
            <div className="text-sm text-foreground"><span className="font-semibold">{callOut.ownerName}</span> <span className="text-xs text-muted-foreground">· {callOut.ownerSubtitle}</span></div>
          </div>
          {renderField("Call Date", "callDate", "date", fmtDate(callOut.callDate))}
          {renderField("Type", "callType", "text", callOut.callType)}
          {renderField("Priority", "priority", "select",
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${PRIORITY_TONES[callOut.priority]}`}>{PRIORITY_LABEL[callOut.priority]}</span>,
            { options: priorityOptions })}
          {renderField("Status", "status", "select",
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_TONES[callOut.status]}`}>{STATUS_LABEL[callOut.status]}</span>,
            { options: statusOptions })}
          {renderField("Engineer", "engineer", "text", callOut.engineer || "—")}
          {renderField("Resolved Date", "resolvedDate", "date", fmtDate(callOut.resolvedDate))}
          {renderField("Site Address", "siteAddress", "textarea",
            callOut.siteAddress ? (
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(callOut.siteAddress)}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-start gap-1" onClick={e => e.stopPropagation()}>
                <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> <span>{callOut.siteAddress}</span>
              </a>
            ) : "—",
            { colSpan: true })}
          {renderField("Site Contact Name", "siteContactName", "text", callOut.siteContactName || "—")}
          {renderField("Site Contact Phone", "siteContactPhone", "text",
            callOut.siteContactPhone ? <a className="text-primary hover:underline" href={`tel:${callOut.siteContactPhone}`}>{callOut.siteContactPhone}</a> : "—")}
          {renderField("Parking", "parkingAvailable", "select",
            callOut.parkingAvailable === "yes" ? "Available on site" : callOut.parkingAvailable === "no" ? "Not available" : "Unknown",
            { options: parkingOptions })}
          {renderField("Description", "description", "textarea",
            callOut.description ? <span className="whitespace-pre-wrap">{callOut.description}</span> : <span className="text-muted-foreground">—</span>,
            { colSpan: true })}
          {renderField("Internal Notes", "notes", "textarea",
            callOut.notes ? <span className="whitespace-pre-wrap">{callOut.notes}</span> : <span className="text-muted-foreground">—</span>,
            { colSpan: true })}
        </div>

        <div className="p-5 md:p-6 border-b border-slate-100 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
          <h3 className="font-bold text-foreground mb-1 flex items-center gap-2"><Upload className="w-4 h-4 text-primary" /> Add Photo, Video or Report</h3>
          <p className="text-xs text-muted-foreground mb-3">Up to 15MB per file. Images and videos appear in the gallery; documents appear under reports.</p>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <label className="group flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white border-2 border-dashed border-primary/40 hover:border-primary hover:bg-primary/5 cursor-pointer transition-colors">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-white text-xs font-semibold shadow-sm group-hover:bg-primary/90 transition-colors flex-shrink-0">
                  <FolderOpen className="w-3.5 h-3.5" /> Choose file
                </span>
                <span className="text-sm text-foreground truncate flex-1 min-w-0">
                  {file ? file.name : <span className="text-muted-foreground">No file selected — click to browse</span>}
                </span>
                <input type="file" accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" />
              </label>
            </div>
            <div className="flex-1 min-w-[180px]">
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Caption / notes (optional)" className="w-full px-3 py-2.5 rounded-lg bg-white border border-slate-200 text-sm" />
            </div>
            <button onClick={upload} disabled={!file || uploading} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-60 transition-colors flex items-center gap-2 shadow-sm">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <>
            <div className="p-5 md:p-6 border-b border-slate-100">
              <h3 className="font-bold text-foreground mb-3 flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-sky-600" /> Reports & Documents <span className="text-xs font-normal text-muted-foreground">({reportItems.length})</span></h3>
              {reportItems.length === 0 ? (
                <div className="text-sm text-muted-foreground">No reports attached.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {reportItems.map(it => (
                    <li key={it.id} className="py-3 flex items-center gap-3">
                      <FolderOpen className="w-5 h-5 text-sky-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{it.fileName}</div>
                        <div className="text-xs text-muted-foreground">{fmtFileSize(it.fileSize)} · {new Date(it.uploadedAt).toLocaleString("en-GB")}{it.notes ? ` · ${it.notes}` : ""}</div>
                      </div>
                      <a href={`${import.meta.env.BASE_URL}api/admin/callouts/attachments/${it.id}/download`} className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-sky-700 hover:bg-sky-50 flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Download</a>
                      <button onClick={() => remove(it.id)} className="px-2 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="p-5 md:p-6">
              <h3 className="font-bold text-foreground mb-3 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-emerald-600" /> Photo & Video Gallery <span className="text-xs font-normal text-muted-foreground">({mediaItems.length})</span></h3>
              {mediaItems.length === 0 ? (
                <div className="text-sm text-muted-foreground">No photos or videos uploaded yet.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {mediaItems.map(it => {
                    const url = `${import.meta.env.BASE_URL}api/admin/callouts/attachments/${it.id}/download`;
                    return (
                      <div key={it.id} className="group relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-square">
                        {isImage(it.mimeType) ? (
                          <img src={url} alt={it.fileName} className="w-full h-full object-cover cursor-zoom-in" onClick={() => setLightbox(it)} />
                        ) : (
                          <video src={url} className="w-full h-full object-cover bg-black cursor-pointer" onClick={() => setLightbox(it)} />
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="text-[11px] text-white font-medium truncate">{it.notes || it.fileName}</div>
                          <div className="text-[10px] text-slate-300">{fmtFileSize(it.fileSize)}</div>
                        </div>
                        {isVideo(it.mimeType) && (
                          <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-semibold flex items-center gap-1"><VideoIcon className="w-3 h-3" /> Video</div>
                        )}
                        <button onClick={() => remove(it.id)} className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 text-red-600 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"><X className="w-6 h-6" /></button>
          <div onClick={e => e.stopPropagation()} className="max-w-6xl max-h-[90vh] w-full flex items-center justify-center">
            {isImage(lightbox.mimeType) ? (
              <img src={`${import.meta.env.BASE_URL}api/admin/callouts/attachments/${lightbox.id}/download`} alt={lightbox.fileName} className="max-w-full max-h-[90vh] object-contain" />
            ) : (
              <video src={`${import.meta.env.BASE_URL}api/admin/callouts/attachments/${lightbox.id}/download`} controls autoPlay className="max-w-full max-h-[90vh]" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NewCallOutModal({ open, onClose, onCreated, defaultDate }: { open: boolean; onClose: () => void; onCreated: () => void; defaultDate?: string }) {
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [ownerKey, setOwnerKey] = useState<string>("");
  const [form, setForm] = useState<any>(emptyCalloutForm());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [files, setFiles] = useState<UploadedFile[]>([]);

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyCalloutForm(), callDate: defaultDate || new Date().toISOString().slice(0, 10) });
    setOwnerKey("");
    setSearch("");
    setFiles([]);
    Promise.all([
      fetch(`${import.meta.env.BASE_URL}api/admin/customers`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
      fetch(`${import.meta.env.BASE_URL}api/admin/companies`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
    ]).then(([cust, comp]) => { setCustomers(cust); setCompanies(comp); }).catch(() => {});
  }, [open, defaultDate]);

  if (!open) return null;

  const ownerOptions = [
    ...customers.map(c => ({ key: `customer-${c.id}`, type: "customer" as const, id: c.id, name: c.name, subtitle: "Domestic" })),
    ...companies.map(c => ({ key: `company-${c.id}`, type: "company" as const, id: c.id, name: c.tradingName, subtitle: c.operatingCompany || "Commercial" })),
  ];
  const q = search.trim().toLowerCase();
  const filteredOwners = q ? ownerOptions.filter(o => o.name.toLowerCase().includes(q) || o.subtitle.toLowerCase().includes(q)) : ownerOptions;
  const selectedOwner = ownerOptions.find(o => o.key === ownerKey) || null;

  const submit = async () => {
    if (!selectedOwner || !form.callDate || !form.callType) return;
    setSaving(true);
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ ownerType: selectedOwner.type, ownerId: selectedOwner.id, ...form }),
      });
      if (!r.ok) { const b = await r.json().catch(() => ({})); alert(b?.error || "Failed to create call-out"); return; }
      const created = await r.json().catch(() => null);
      if (created?.id && files.length) {
        await Promise.all(files.map(f => fetch(`${import.meta.env.BASE_URL}api/admin/callouts/${created.id}/attachments`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ fileName: f.name, mimeType: f.mimeType, fileSize: f.size, data: f.data }),
        }).catch(() => null)));
      }
      onCreated();
      onClose();
    } catch { alert("Failed to create call-out"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-600" /><h3 className="font-bold text-foreground">New Call Out</h3></div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client / Company *</label>
            {selectedOwner ? (
              <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/30 bg-primary/5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedOwner.type === "company" ? "bg-primary/10 text-primary" : "bg-emerald-50 text-emerald-600"}`}>
                  {selectedOwner.type === "company" ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}
                </div>
                <div className="flex-1"><div className="font-semibold text-sm text-foreground">{selectedOwner.name}</div><div className="text-xs text-muted-foreground">{selectedOwner.subtitle}</div></div>
                <button onClick={() => setOwnerKey("")} className="text-xs text-primary font-medium hover:underline">Change</button>
              </div>
            ) : (
              <>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients & companies..." className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {filteredOwners.length === 0 ? (
                    <div className="p-3 text-xs text-muted-foreground text-center">No matches</div>
                  ) : filteredOwners.slice(0, 30).map(o => (
                    <button key={o.key} onClick={() => setOwnerKey(o.key)} className="w-full text-left px-3 py-2 hover:bg-primary/5 flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${o.type === "company" ? "bg-primary/10 text-primary" : "bg-emerald-50 text-emerald-600"}`}>
                        {o.type === "company" ? <Building2 className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
                      </div>
                      <div className="flex-1"><div className="text-sm font-medium text-foreground">{o.name}</div><div className="text-xs text-muted-foreground">{o.subtitle}</div></div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Call Date *</label>
              <input type="date" value={form.callDate} onChange={e => setForm({ ...form, callDate: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Call Type *</label>
              <input value={form.callType} onChange={e => setForm({ ...form, callType: e.target.value })} placeholder="e.g. AC not cooling" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                {CALLOUT_PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                {CALLOUT_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Engineer</label>
              <input value={form.engineer} onChange={e => setForm({ ...form, engineer: e.target.value })} placeholder="Assigned engineer" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Site Contact</label>
              <input value={form.siteContactName} onChange={e => setForm({ ...form, siteContactName: e.target.value })} placeholder="Name on site" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Site Address</label>
              <input value={form.siteAddress} onChange={e => setForm({ ...form, siteAddress: e.target.value })} placeholder="Where the engineer is going" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Issue details" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
            </div>
          </div>
          <FileUploader files={files} onChange={setFiles} label="Attach files (photos, PDF, Excel)" />
          <div className="flex items-center gap-2 pt-2">
            <button onClick={submit} disabled={saving || !selectedOwner || !form.callDate || !form.callType} className="flex-1 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 disabled:opacity-40 transition-all">
              {saving ? "Creating..." : "Create Call Out"}
            </button>
            <button onClick={onClose} className="px-4 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-medium text-sm hover:bg-slate-200">Cancel</button>
          </div>
          <p className="text-[11px] text-muted-foreground">A matching event will appear on the Calendar automatically.</p>
        </div>
      </div>
    </div>
  );
}

interface CalAttachment { id: number; eventId: number; fileName: string; mimeType: string; fileSize: number; uploadedAt: string }

function ScheduleEditModal({ event, onClose, onChanged }: { event: CalEvent; onClose: () => void; onChanged: () => void }) {
  const [title, setTitle] = useState(event.title);
  const [date, setDate] = useState(event.eventDate);
  const [time, setTime] = useState(event.eventTime || "");
  const [eventType, setEventType] = useState(event.eventType || "appointment");
  const [desc, setDesc] = useState(event.description || "");
  const [status, setStatus] = useState(event.status || "scheduled");
  const [engineer, setEngineer] = useState(event.engineer || "");
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<CalAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const isLinked = event.eventType === "callout" || event.eventType === "ppm";

  const loadAttachments = () => {
    fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${event.id}/attachments`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(d => Array.isArray(d) && setAttachments(d))
      .catch(() => {});
  };
  useEffect(() => { loadAttachments(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [event.id]);

  const uploadFiles = async (files: UploadedFile[]) => {
    if (!files.length) return;
    setUploading(true);
    try {
      await Promise.all(files.map(f => fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${event.id}/attachments`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ fileName: f.name, mimeType: f.mimeType, fileSize: f.size, data: f.data }),
      }).catch(() => null)));
      loadAttachments();
    } finally { setUploading(false); }
  };
  const removeAttachment = async (id: number) => {
    if (!confirm("Delete this attachment?")) return;
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/attachments/${id}`, { method: "DELETE", credentials: "include" });
      if (r.ok) setAttachments(prev => prev.filter(a => a.id !== id));
    } catch {}
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${event.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ title, eventDate: date, eventTime: time || null, eventType, description: desc || null, status, engineer: engineer || null }),
      });
      if (!r.ok) { alert("Failed to update"); return; }
      onChanged();
      onClose();
    } catch { alert("Failed to update"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    const warn = isLinked
      ? `This schedule is linked to a ${eventType === "callout" ? "Call Out" : "PPM"} record. Deleting it removes the calendar entry only — the underlying record will remain. Continue?`
      : "Delete this schedule?";
    if (!confirm(warn)) return;
    setSaving(true);
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/calendar/${event.id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) { alert("Failed to delete schedule"); return; }
      onChanged();
      onClose();
    } catch { alert("Failed to delete schedule"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8" onClick={e => e.stopPropagation()}>
        <div className={`px-6 py-4 ${EVENT_TYPE_COLORS[eventType] || "bg-slate-500"} text-white rounded-t-2xl flex items-center justify-between`}>
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">{EVENT_TYPE_LABELS[eventType] || eventType}</div>
            <h3 className="font-bold text-lg leading-tight">{event.title}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          {(event.createdByName || event.createdAt) && (
            <div className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <User className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Created by</span>
              <span className="font-semibold text-foreground">{event.createdByName || "Unknown"}</span>
              <span className="text-muted-foreground ml-auto">{formatDate(event.createdAt)}</span>
            </div>
          )}
          {isLinked && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              This schedule is linked to a {eventType === "callout" ? "Call Out" : "PPM"} record. Editing the underlying record will refresh this entry.
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Time</label>
              <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</label>
              <select value={eventType} onChange={e => setEventType(e.target.value)} disabled={isLinked} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50">
                {Object.keys(EVENT_TYPE_LABELS).map(k => <option key={k} value={k}>{EVENT_TYPE_LABELS[k]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Engineer</label>
            <input value={engineer} onChange={e => setEngineer(e.target.value)} placeholder="Engineer attending" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
          </div>

          <div className="border-t border-slate-100 pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attachments ({attachments.length})</span>
              </div>
              {uploading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            </div>
            {attachments.length > 0 && (
              <ul className="space-y-1.5">
                {attachments.map(a => (
                  <li key={a.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-sm">
                    <FileText className="w-4 h-4 text-primary shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-foreground">{a.fileName}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{(a.fileSize / 1024).toFixed(0)} KB</span>
                    <a href={`${import.meta.env.BASE_URL}api/admin/calendar/attachments/${a.id}/download`} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-slate-200 text-muted-foreground hover:text-primary" title="Download"><Download className="w-3.5 h-3.5" /></a>
                    <button onClick={() => removeAttachment(a.id)} className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
            <FileUploader files={[]} onChange={uploadFiles} label={attachments.length > 0 ? "Add more files" : "Add files (photos, PDF, Excel)"} />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button onClick={save} disabled={saving || !title || !date} className="flex-1 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 disabled:opacity-40">
              {saving ? "Saving..." : "Save changes"}
            </button>
            <button onClick={remove} disabled={saving} className="px-3 py-2.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 font-medium text-sm flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Delete</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SchedulesOverview() {
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [rangeFilter, setRangeFilter] = useState<"upcoming" | "past" | "all">("upcoming");
  const [search, setSearch] = useState("");
  const [showNewCallout, setShowNewCallout] = useState(false);
  const [editing, setEditing] = useState<CalEvent | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/calendar`, { credentials: "include" });
      if (r.ok) setEvents(await r.json());
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const todayStr = new Date().toISOString().slice(0, 10);
  const counts: Record<string, number> = { all: events.length };
  Object.keys(EVENT_TYPE_LABELS).forEach(k => { counts[k] = 0; });
  events.forEach(e => { const k = e.eventType || "appointment"; counts[k] = (counts[k] || 0) + 1; });

  const filtered = events.filter(e => {
    if (typeFilter !== "all" && (e.eventType || "appointment") !== typeFilter) return false;
    if (rangeFilter === "upcoming" && e.eventDate < todayStr) return false;
    if (rangeFilter === "past" && e.eventDate >= todayStr) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${e.title} ${e.description || ""} ${e.eventType || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => rangeFilter === "past" ? b.eventDate.localeCompare(a.eventDate) : a.eventDate.localeCompare(b.eventDate));

  const fmtDate = (d: string) => new Date(d + "T00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  const upcomingCount = events.filter(e => e.eventDate >= todayStr).length;
  const todayCount = events.filter(e => e.eventDate === todayStr).length;
  const calloutCount = events.filter(e => e.eventType === "callout" && e.status !== "completed" && e.status !== "cancelled").length;

  return (
    <div className="space-y-6">
      {editing && <ScheduleEditModal event={editing} onClose={() => setEditing(null)} onChanged={load} />}
      <NewCallOutModal open={showNewCallout} onClose={() => setShowNewCallout(false)} onCreated={load} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Schedules" value={events.length} icon={CalendarDays} color="bg-blue-50 text-blue-600" />
        <StatCard label="Today" value={todayCount} icon={Clock} color="bg-amber-50 text-amber-600" />
        <StatCard label="Upcoming" value={upcomingCount} icon={CalendarDays} color="bg-emerald-50 text-emerald-600" />
        <StatCard label="Active Call Outs" value={calloutCount} icon={AlertTriangle} color="bg-rose-50 text-rose-600" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-bold text-foreground">All Schedules</h2>
            <p className="text-xs text-muted-foreground">Every event from the calendar, tagged by type</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowNewCallout(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 transition-all">
              <AlertTriangle className="w-4 h-4" /> New Call Out
            </button>
            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
              {(["upcoming", "past", "all"] as const).map(r => (
                <button key={r} onClick={() => setRangeFilter(r)} className={`px-3 py-1.5 text-xs font-semibold transition-all capitalize ${rangeFilter === r ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>{r}</button>
              ))}
            </div>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, notes..." className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
          </div>
        </div>

        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <button onClick={() => setTypeFilter("all")} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${typeFilter === "all" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
            All <span className="ml-1 opacity-70">{counts.all}</span>
          </button>
          {Object.keys(EVENT_TYPE_LABELS).map(k => {
            const active = typeFilter === k;
            const dot = EVENT_TYPE_COLORS[k] || "bg-slate-500";
            return (
              <button key={k} onClick={() => setTypeFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-1.5 ${active ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
                <span className={`w-2 h-2 rounded-full ${dot}`} />
                {EVENT_TYPE_LABELS[k]} <span className="opacity-70">{counts[k] || 0}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-muted-foreground">{events.length === 0 ? "No schedules yet. Add events from the Calendar." : "No schedules match your filters."}</p>
          </div>
        ) : (
          <>
            <div className="md:hidden divide-y divide-slate-100">
              {filtered.map(e => {
                const t = e.eventType || "appointment";
                return (
                  <button key={e.id} onClick={() => setEditing(e)} className="w-full text-left p-4 hover:bg-primary/5 active:bg-primary/10 transition-colors block">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${EVENT_TYPE_COLORS[t] || "bg-slate-500"} text-white`}>
                        <CalendarDays className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${EVENT_TYPE_COLORS[t] || "bg-slate-500"}`}>{EVENT_TYPE_LABELS[t] || t}</span>
                          {e.status && e.status !== "scheduled" && (
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${e.status === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"} capitalize`}>{e.status}</span>
                          )}
                          <span className="text-[11px] text-muted-foreground ml-auto">{fmtDate(e.eventDate)}{e.eventTime ? ` · ${e.eventTime}` : ""}</span>
                        </div>
                        <div className="font-semibold text-foreground text-sm leading-tight">{e.title}</div>
                        {e.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{e.description}</div>}
                        <div className="flex items-center gap-3 flex-wrap mt-1">
                          {e.engineer && <div className="text-[11px] text-primary font-semibold inline-flex items-center gap-1"><Wrench className="w-3 h-3" />{e.engineer}</div>}
                          {e.createdByName && <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><User className="w-3 h-3" />{e.createdByName}</div>}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Time</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">Title</th>
                    <th className="px-4 py-3 font-semibold">Engineer</th>
                    <th className="px-4 py-3 font-semibold">Created By</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(e => {
                    const t = e.eventType || "appointment";
                    return (
                      <tr key={e.id} onClick={() => setEditing(e)} className="border-b border-slate-100 last:border-0 hover:bg-primary/5 cursor-pointer transition-colors group" title="Open schedule">
                        <td className="px-4 py-3 whitespace-nowrap text-foreground font-medium">{fmtDate(e.eventDate)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{e.eventTime || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold text-white ${EVENT_TYPE_COLORS[t] || "bg-slate-500"}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
                            {EVENT_TYPE_LABELS[t] || t}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          <div className="font-medium group-hover:text-primary transition-colors">{e.title}</div>
                          {e.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1 max-w-xl">{e.description}</div>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs">
                          {e.engineer ? (
                            <span className="inline-flex items-center gap-1.5 text-primary font-semibold"><Wrench className="w-3 h-3" />{e.engineer}</span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                          {e.createdByName ? (
                            <span className="inline-flex items-center gap-1.5"><User className="w-3 h-3" />{e.createdByName}</span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                            e.status === "completed" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                            e.status === "cancelled" ? "bg-red-50 text-red-700 border border-red-200" :
                            "bg-blue-50 text-blue-700 border border-blue-200"
                          }`}>{e.status || "scheduled"}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CallOutsOverview() {
  const [rows, setRows] = useState<CalloutWithOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | CalloutStatus>("all");
  const [priorityFilter, setPriorityFilter] = useState<"all" | CalloutPriority>("all");
  const [selected, setSelected] = useState<CalloutWithOwner | null>(null);
  const [attachCounts, setAttachCounts] = useState<Record<number, { media: number; reports: number }>>({});
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [customersRes, companiesRes] = await Promise.all([
        fetch(`${import.meta.env.BASE_URL}api/admin/customers`, { credentials: "include" }),
        fetch(`${import.meta.env.BASE_URL}api/admin/companies`, { credentials: "include" }),
      ]);
      const customers: CustomerRow[] = customersRes.ok ? await customersRes.json() : [];
      const companies: CompanyRow[] = companiesRes.ok ? await companiesRes.json() : [];

      const fetches = await Promise.all([
        ...customers.map(c => fetch(`${import.meta.env.BASE_URL}api/admin/callouts?ownerType=customer&ownerId=${c.id}`, { credentials: "include" }).then(r => r.ok ? r.json() : [])),
        ...companies.map(c => fetch(`${import.meta.env.BASE_URL}api/admin/callouts?ownerType=company&ownerId=${c.id}`, { credentials: "include" }).then(r => r.ok ? r.json() : [])),
      ]);

      const all: CalloutWithOwner[] = [];
      customers.forEach((c, i) => {
        (fetches[i] || []).forEach((co: CalloutRow) => {
          all.push({ ...co, ownerName: c.name, ownerSubtitle: "Domestic" });
        });
      });
      companies.forEach((c, i) => {
        (fetches[customers.length + i] || []).forEach((co: CalloutRow) => {
          all.push({ ...co, ownerName: c.tradingName, ownerSubtitle: c.operatingCompany || "Commercial" });
        });
      });
      all.sort((a, b) => (b.callDate || "").localeCompare(a.callDate || ""));
      setRows(all);
      const counts = await Promise.all(all.map(async r => {
        try {
          const ar = await fetch(`${import.meta.env.BASE_URL}api/admin/callouts/${r.id}/attachments`, { credentials: "include" });
          if (!ar.ok) return [r.id, { media: 0, reports: 0 }] as const;
          const list: CalloutAttachment[] = await ar.json();
          return [r.id, { media: list.filter(a => isMedia(a.mimeType)).length, reports: list.filter(a => !isMedia(a.mimeType)).length }] as const;
        } catch { return [r.id, { media: 0, reports: 0 }] as const; }
      }));
      setAttachCounts(Object.fromEntries(counts));
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const totals = {
    open: rows.filter(r => r.status === "open").length,
    in_progress: rows.filter(r => r.status === "in_progress").length,
    resolved: rows.filter(r => r.status === "resolved").length,
    emergency: rows.filter(r => r.priority === "emergency" && r.status !== "resolved").length,
  };

  const filtered = rows.filter(r => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (priorityFilter !== "all" && r.priority !== priorityFilter) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${r.ownerName} ${r.ownerSubtitle} ${r.callType} ${r.engineer || ""} ${r.siteAddress || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="space-y-6">
      {selected && <CallOutDetailModal callOut={selected} onClose={() => setSelected(null)} onChanged={load} />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Open" value={totals.open} icon={AlertTriangle} color="bg-red-50 text-red-600" />
        <StatCard label="In Progress" value={totals.in_progress} icon={Clock} color="bg-amber-50 text-amber-600" />
        <StatCard label="Resolved" value={totals.resolved} icon={Check} color="bg-emerald-50 text-emerald-600" />
        <StatCard label="Emergency Active" value={totals.emergency} icon={AlertTriangle} color="bg-red-100 text-red-700" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-bold text-foreground">All Call Outs</h2>
            <p className="text-xs text-muted-foreground">Reactive visits & emergency call-outs across every client</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowNew(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-all">
              <Plus className="w-4 h-4" /> New Call Out
            </button>
            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
              {(["all", "open", "in_progress", "resolved"] as const).map(f => (
                <button key={f} onClick={() => setStatusFilter(f)} className={`px-3 py-1.5 text-xs font-semibold transition-all ${statusFilter === f ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  {f === "all" ? "All" : f === "in_progress" ? "In Progress" : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value as any)} className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary">
              <option value="all">All priorities</option>
              <option value="emergency">Emergency</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search owner, type, engineer..." className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
          </div>
        </div>
        <NewCallOutModal open={showNew} onClose={() => setShowNew(false)} onCreated={load} />

        {loading ? (
          <div className="text-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-muted-foreground">{rows.length === 0 ? "No call-outs logged yet." : "No call-outs match your filters."}</p>
          </div>
        ) : (
          <>
            <div className="md:hidden divide-y divide-slate-100">
              {filtered.map(r => {
                const counts = attachCounts[r.id];
                return (
                  <button key={r.id} onClick={() => setSelected(r)}
                    className="w-full text-left p-4 hover:bg-primary/5 active:bg-primary/10 transition-colors block">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${r.ownerType === "company" ? "bg-primary/10 text-primary" : "bg-emerald-50 text-emerald-600"}`}>
                        {r.ownerType === "company" ? <Building2 className="w-5 h-5" /> : <User className="w-5 h-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-bold ${PRIORITY_TONES[r.priority]}`}>{PRIORITY_LABEL[r.priority]}</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-bold ${STATUS_TONES[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                          <span className="text-[11px] text-muted-foreground ml-auto">{fmtDate(r.callDate)}</span>
                        </div>
                        <div className="font-semibold text-foreground text-sm leading-tight">{r.callType}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {r.ownerName} <span className="text-slate-400">· {r.ownerSubtitle}</span>
                        </div>
                        {r.siteAddress && (
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r.siteAddress)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[11px] text-primary hover:underline mt-0.5 line-clamp-1 block" title="Open directions in Google Maps">📍 {r.siteAddress}</a>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                          {r.engineer && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{r.engineer}</span>}
                          {(counts?.media ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><ImageIcon className="w-3 h-3" />{counts!.media}</span>
                          )}
                          {(counts?.reports ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-1 text-sky-700 font-semibold"><Paperclip className="w-3 h-3" />{counts!.reports}</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Owner</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">Priority</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Engineer</th>
                    <th className="px-4 py-3 font-semibold">Raised By</th>
                    <th className="px-4 py-3 font-semibold">Resolved</th>
                    <th className="px-4 py-3 font-semibold">Files</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id}
                      onClick={() => setSelected(r)}
                      className="border-b border-slate-100 last:border-0 hover:bg-primary/5 cursor-pointer transition-colors group"
                      title="Open call-out details">
                      <td className="px-4 py-3 text-foreground whitespace-nowrap">{fmtDate(r.callDate)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5 min-w-[12rem]">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${r.ownerType === "company" ? "bg-primary/10 text-primary" : "bg-emerald-50 text-emerald-600"}`}>
                            {r.ownerType === "company" ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-foreground leading-tight group-hover:text-primary transition-colors">{r.ownerName}</div>
                            <div className="text-xs text-muted-foreground">{r.ownerSubtitle}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground font-medium">
                        {r.callType}
                        {r.siteAddress && (
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r.siteAddress)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-xs text-primary hover:underline font-normal mt-0.5 line-clamp-1 block" title="Open directions in Google Maps">📍 {r.siteAddress}</a>
                        )}
                      </td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${PRIORITY_TONES[r.priority]}`}>{PRIORITY_LABEL[r.priority]}</span></td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_TONES[r.status]}`}>{STATUS_LABEL[r.status]}</span></td>
                      <td className="px-4 py-3 text-foreground whitespace-nowrap">{r.engineer || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {r.createdByName ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-xs font-medium text-slate-700"><User className="w-3 h-3" />{r.createdByName}</span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(r.resolvedDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {(attachCounts[r.id]?.media ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold"><ImageIcon className="w-3 h-3" />{attachCounts[r.id].media}</span>
                          )}
                          {(attachCounts[r.id]?.reports ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold"><Paperclip className="w-3 h-3" />{attachCounts[r.id].reports}</span>
                          )}
                          {!attachCounts[r.id] || (attachCounts[r.id].media + attachCounts[r.id].reports === 0) ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PpmOwnerDetail({ owner, onBack }: { owner: PpmOwnerRow; onBack: () => void }) {
  const [rows, setRows] = useState<PpmRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/ppm?ownerType=${owner.ownerType}&ownerId=${owner.ownerId}`, { credentials: "include" });
      if (res.ok) setRows(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [owner.ownerType, owner.ownerId]);

  const byType = new Map(rows.map(r => [r.serviceType, r]));
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const chartData = PPM_SERVICES.map(s => {
    const row = byType.get(s.key);
    let days: number | null = null;
    if (row?.dueDate) {
      const due = new Date(row.dueDate); due.setHours(0, 0, 0, 0);
      days = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    }
    return { name: s.label, short: s.label.split(" ")[0], days };
  });

  const counts = { Overdue: 0, Due: 0, OK: 0, Missing: 0 } as Record<string, number>;
  PPM_SERVICES.forEach(s => { counts[ppmStatus(byType.get(s.key)).label]++; });

  const pieData = [
    { name: "Overdue", value: counts.Overdue, color: "#dc2626" },
    { name: "Due Soon", value: counts.Due, color: "#f59e0b" },
    { name: "In Date", value: counts.OK, color: "#10b981" },
    { name: "Missing", value: counts.Missing, color: "#94a3b8" },
  ].filter(p => p.value > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
          <ChevronLeft className="w-4 h-4" /> Back to all schedules
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${owner.ownerType === "company" ? "bg-primary/10 text-primary" : "bg-emerald-50 text-emerald-600"}`}>
            {owner.ownerType === "company" ? <Building2 className="w-6 h-6" /> : <User className="w-6 h-6" />}
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{owner.ownerType === "company" ? "Commercial" : "Domestic"}</div>
            <h2 className="text-xl font-bold text-foreground leading-tight">{owner.name}</h2>
            <p className="text-sm text-muted-foreground">{owner.subtitle}</p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs">
            {counts.Overdue > 0 && <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-700 font-semibold border border-red-200">{counts.Overdue} overdue</span>}
            {counts.Due > 0 && <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold border border-amber-200">{counts.Due} due</span>}
            {counts.OK > 0 && <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-200">{counts.OK} ok</span>}
            {counts.Missing > 0 && <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold border border-slate-200">{counts.Missing} missing</span>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-bold text-foreground mb-1">Maintenance Status</h3>
          <p className="text-xs text-muted-foreground mb-4">Days until next service due — negative bars are overdue</p>
          {loading ? (
            <div className="h-[220px] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : chartData.every(d => d.days === null) ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">No due dates set yet — use the table below to schedule services.</div>
          ) : (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={chartData.filter(d => d.days !== null)} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="short" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} label={{ value: "Days", angle: -90, position: "insideLeft", style: { fill: "#64748b", fontSize: 11 } }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v: any) => [`${v} days`, (v as number) < 0 ? "Overdue by" : "Due in"]}
                    labelFormatter={(l, payload) => (payload && payload[0] ? (payload[0].payload as any).name : l)}
                  />
                  <Bar dataKey="days" radius={[4, 4, 0, 0]}>
                    {chartData.filter(d => d.days !== null).map((entry, i) => (
                      <Cell key={i} fill={entry.days! < 0 ? "#dc2626" : entry.days! <= 30 ? "#f59e0b" : "#10b981"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-bold text-foreground mb-1">Compliance</h3>
          <p className="text-xs text-muted-foreground mb-4">Across all 6 services</p>
          {pieData.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">No data.</div>
          ) : (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={75} paddingAngle={2}>
                    {pieData.map((p, i) => <Cell key={i} fill={p.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <PpmSection ownerType={owner.ownerType} ownerId={owner.ownerId} />
      <CallOutsSection ownerType={owner.ownerType} ownerId={owner.ownerId} />
    </div>
  );
}

export default function AdminDashboard() {
  const [, setLocation] = useLocation();
  const [sidebarView, setSidebarViewRaw] = useState<SidebarView>("home");
  const [nlOpen, setNlOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  type AdminTheme = { sidebarBg: string; sidebarText: string; mainBg: string; sidebarHoverBg: string; sidebarHoverText: string; sidebarActiveBg: string; sidebarActiveText: string };
  const THEME_DEFAULTS: AdminTheme = { sidebarBg: "#ffffff", sidebarText: "#475569", mainBg: "#f8fafc", sidebarHoverBg: "#f1f5f9", sidebarHoverText: "#0f172a", sidebarActiveBg: "#1d4ed8", sidebarActiveText: "#ffffff" };
  const [theme, setTheme] = useState<AdminTheme>(() => {
    try { const raw = localStorage.getItem("admin-theme"); if (raw) return { ...THEME_DEFAULTS, ...JSON.parse(raw) }; } catch {}
    return THEME_DEFAULTS;
  });
  const themeLoadedRef = useRef(false);
  const skipNextThemeSaveRef = useRef(false);
  useEffect(() => { try { localStorage.setItem("admin-theme", JSON.stringify(theme)); } catch {} }, [theme]);
  const [themeOpen, setThemeOpen] = useState(false);
  const isDark = (hex: string) => {
    const h = hex.replace("#", ""); if (h.length !== 6) return false;
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
  };
  const isDarkSidebar = isDark(theme.sidebarBg);
  const sidebarBorder = isDarkSidebar ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)";
  const setSidebarView = (v: SidebarView) => { setSidebarViewRaw(v); setMobileNavOpen(false); };
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>("callback");
  const [scheduleSubmission, setScheduleSubmission] = useState<Submission | null>(null);

  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [analyticsTab, setAnalyticsTab] = useState<"overview" | "visitors">("overview");

  const [calendarKey, setCalendarKey] = useState(0);

  const [me, setMe] = useState<{ id: number; name: string; email: string; phone?: string | null; avatar?: string | null; theme?: Record<string, string> | null; role?: string } | null>(null);
  useEffect(() => {
    // Only hydrate once /admin/me has actually returned (theme key will be present,
    // even if its value is null). The partial `me` set from /admin/check omits theme,
    // so this gate prevents an empty/local theme from racing the server value.
    if (!me || !("theme" in me) || themeLoadedRef.current) return;
    themeLoadedRef.current = true;
    if (me.theme && typeof me.theme === "object" && Object.keys(me.theme).length > 0) {
      skipNextThemeSaveRef.current = true; // ignore the save triggered by this hydration
      setTheme(t => ({ ...THEME_DEFAULTS, ...t, ...(me.theme as Record<string, string>) }));
    }
  }, [me]);
  useEffect(() => {
    if (!themeLoadedRef.current) return;
    if (skipNextThemeSaveRef.current) { skipNextThemeSaveRef.current = false; return; }
    const handle = setTimeout(() => {
      fetch(`${import.meta.env.BASE_URL}api/admin/me`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme }),
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(handle);
  }, [theme]);
  const refreshMe = async () => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/me`, { credentials: "include" });
      if (res.ok) setMe(await res.json());
    } catch {}
  };

  const checkAuth = async () => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/check`, { credentials: "include" });
      if (!res.ok) { setLocation("/admin"); return false; }
      const data = await res.json();
      if (data.admin?.role === "engineer") { setLocation("/engineer"); return false; }
      if (data.admin) setMe(prev => prev ?? { id: data.admin.id, name: data.admin.name, email: data.admin.email, role: data.admin.role });
      refreshMe();
      return true;
    } catch { setLocation("/admin"); return false; }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/submissions`, { credentials: "include" });
      if (!res.ok) { setLocation("/admin"); return; }
      setSubmissions(await res.json());
    } catch { setLocation("/admin"); }
    finally { setLoading(false); }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/analytics?days=${days}`, { credentials: "include" });
      if (res.ok) setAnalyticsData(await res.json());
    } catch {}
    finally { setAnalyticsLoading(false); }
  };

  useEffect(() => { checkAuth().then(ok => { if (ok) fetchSubmissions(); }); }, []);
  useEffect(() => { if (sidebarView === "analytics") fetchAnalytics(); }, [sidebarView, days]);

  const handleLogout = async () => {
    await fetch(`${import.meta.env.BASE_URL}api/admin/logout`, { method: "POST", credentials: "include" });
    setLocation("/admin");
  };

  const handleDelete = (id: number) => setSubmissions(prev => prev.filter(s => s.id !== id));
  const handleUpdate = (id: number, data: Partial<Submission>) => setSubmissions(prev => prev.map(s => s.id === id ? { ...s, ...data } : s));

  const callbackSubs = submissions.filter(s => classifySubmission(s.formType) === "callback");
  const quoteSubs = submissions.filter(s => classifySubmission(s.formType) === "quote");
  const contactSubs = submissions.filter(s => classifySubmission(s.formType) === "contact");

  const tabs: { key: Tab; label: string; icon: typeof PhoneCall; count: number }[] = [
    { key: "callback", label: "Callback Requests", icon: PhoneCall, count: callbackSubs.length },
    { key: "quote", label: "Quote Requests", icon: FileText, count: quoteSubs.length },
    { key: "contact", label: "Contact", icon: Mail, count: contactSubs.length },
  ];

  const activeSubs = activeTab === "callback" ? callbackSubs : activeTab === "quote" ? quoteSubs : contactSubs;

  const avgViewsPerDay = analyticsData ? Math.round(analyticsData.totalViews / days) : 0;
  const callClicks = analyticsData ? analyticsData.events.filter(e => e.name.startsWith("call_click")).reduce((s, e) => s + e.value, 0) : 0;

  const sidebarItems: { key: SidebarView; label: string; icon: typeof BarChart3 }[] = [
    { key: "home", label: "Dashboard", icon: BarChart3 },
    { key: "forms", label: "Forms", icon: FileText },
    { key: "calendar", label: "Calendar", icon: CalendarDays },
    { key: "analytics", label: "Website Analytics", icon: TrendingUp },
    { key: "pages", label: "Pages", icon: FileText },
    { key: "collections", label: "Collections", icon: FileText },
    { key: "templates", label: "Programmatic SEO", icon: FileText },
  ];

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: theme.mainBg }} data-admin-sidebar-dark={isDarkSidebar ? "true" : "false"}>
      {mobileNavOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileNavOpen(false)} />
      )}
      <aside
        className={`w-64 flex flex-col shrink-0 fixed lg:sticky top-0 h-screen z-50 transition-transform lg:translate-x-0 admin-sidebar ${mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} ${isDarkSidebar ? "admin-sidebar-dark" : ""}`}
        style={{ backgroundColor: theme.sidebarBg, color: theme.sidebarText, borderRight: `1px solid ${sidebarBorder}` }}
      >
        <div className="px-4 py-4 flex items-start justify-between gap-2" style={{ borderBottom: `1px solid ${sidebarBorder}` }}>
          <div className="min-w-0 flex-1">
            <img src={logo} alt="VertexAir" className="w-full h-auto object-contain" />
            <p className="text-xs mt-1.5 font-medium opacity-70" style={{ color: theme.sidebarText }}>Admin Panel</p>
          </div>
          <button onClick={() => setMobileNavOpen(false)} className="lg:hidden p-1.5 -mr-1 -mt-1 rounded-lg hover:bg-slate-100 text-slate-500 flex-shrink-0" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {sidebarItems.map(item => {
            const Icon = item.icon;
            const isActive = sidebarView === item.key;
            return (
              <button key={item.key} onClick={() => setSidebarView(item.key)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
                style={isActive ? { backgroundColor: theme.sidebarActiveBg, color: theme.sidebarActiveText, boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)" } : { color: theme.sidebarText }}
                onMouseEnter={e => { if (!isActive) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = theme.sidebarHoverBg; el.style.color = theme.sidebarHoverText; } }}
                onMouseLeave={e => { if (!isActive) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = "transparent"; el.style.color = theme.sidebarText; } }}
              >
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}

          <div className="my-3" style={{ borderTop: `1px solid ${sidebarBorder}` }} />

          {([
            { key: "clients" as SidebarView, label: "Domestic", icon: Users },
            { key: "company" as SidebarView, label: "Commercial", icon: Building2 },
            { key: "sites" as SidebarView, label: "Sites", icon: MapPin, isLink: true, path: "/admin/sites" },
            { key: "engineer-jobs" as SidebarView, label: "Engineer Jobs", icon: Wrench, isLink: true, path: "/admin/engineer-jobs" },
            { key: "ppm" as SidebarView, label: "PPM Schedules", icon: ClipboardList },
            { key: "engineer-ppm" as SidebarView, label: "Engineer PPM", icon: Wrench, isLink: true, path: "/admin/engineer-ppm" },
            { key: "callouts" as SidebarView, label: "Schedules", icon: AlertTriangle },
          ]).map(item => {
            const Icon = item.icon;
            const isActive = sidebarView === item.key;
            if (item.isLink) {
              return (
                <button
                  key={item.key}
                  onClick={() => setLocation(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 text-slate-500 hover:bg-slate-100/50 hover:text-slate-900 group relative`}
                >
                  <Icon className={`w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors`} />
                  <span className="flex-1 text-left">{item.label}</span>
                </button>
              );
            }
            return (
              <button key={item.key} onClick={() => setSidebarView(item.key)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
                style={isActive ? { backgroundColor: theme.sidebarActiveBg, color: theme.sidebarActiveText, boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)" } : { color: theme.sidebarText }}
                onMouseEnter={e => { if (!isActive) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = theme.sidebarHoverBg; el.style.color = theme.sidebarHoverText; } }}
                onMouseLeave={e => { if (!isActive) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = "transparent"; el.style.color = theme.sidebarText; } }}
              >
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}

          <div className="my-3" style={{ borderTop: `1px solid ${sidebarBorder}` }} />

          {(() => {
            const isNl = (sidebarView as string).startsWith("nl-");
            const subs: { key: NlSubView; label: string }[] = [
              { key: "nl-dashboard", label: "Dashboard" },
              { key: "nl-subscribers", label: "Subscribers" },
              { key: "nl-templates", label: "Email Templates" },
              { key: "nl-campaigns", label: "Campaigns" },
              { key: "nl-smtp", label: "SMTP Test" },
              { key: "nl-reports", label: "Reports" },
              { key: "nl-settings", label: "Settings" },
            ];
            const open = nlOpen || isNl;
            return (
              <>
                <button onClick={() => { if (isNl) { setNlOpen(o => !o); } else { setNlOpen(true); setSidebarView("nl-dashboard"); } }}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
                  style={isNl ? { backgroundColor: theme.sidebarActiveBg + "26", color: theme.sidebarActiveBg } : { color: theme.sidebarText }}
                  onMouseEnter={e => { if (!isNl) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = theme.sidebarHoverBg; el.style.color = theme.sidebarHoverText; } }}
                  onMouseLeave={e => { if (!isNl) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = "transparent"; el.style.color = theme.sidebarText; } }}
                >
                  <span className="flex items-center gap-3"><Megaphone className="w-4 h-4" /> Newsletter</span>
                  {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {open && (
                  <div className="mt-1 ml-3 pl-3 space-y-0.5" style={{ borderLeft: `1px solid ${sidebarBorder}` }}>
                    {subs.map(s => {
                      const isActiveSub = sidebarView === s.key;
                      return (
                        <button key={s.key} onClick={() => setSidebarView(s.key)}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all"
                          style={isActiveSub ? { backgroundColor: theme.sidebarActiveBg, color: theme.sidebarActiveText, boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)" } : { color: theme.sidebarText }}
                          onMouseEnter={e => { if (!isActiveSub) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = theme.sidebarHoverBg; el.style.color = theme.sidebarHoverText; } }}
                          onMouseLeave={e => { if (!isActiveSub) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = "transparent"; el.style.color = theme.sidebarText; } }}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}

        </nav>
        <div className="px-3 py-4 space-y-1 relative" style={{ borderTop: `1px solid ${sidebarBorder}` }}>
          {(() => {
            const isActive = sidebarView === "profile";
            return (
              <button onClick={() => setSidebarView("profile")}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
                style={isActive ? { backgroundColor: theme.sidebarActiveBg, color: theme.sidebarActiveText, boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)" } : { color: theme.sidebarText }}
                onMouseEnter={e => { if (!isActive) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = theme.sidebarHoverBg; el.style.color = theme.sidebarHoverText; } }}
                onMouseLeave={e => { if (!isActive) { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = "transparent"; el.style.color = theme.sidebarText; } }}
              >
                <UserCircle2 className="w-4 h-4" /> My Profile
              </button>
            );
          })()}
          <button onClick={() => setThemeOpen(o => !o)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
            style={{ color: theme.sidebarText }}
            onMouseEnter={e => { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = theme.sidebarHoverBg; el.style.color = theme.sidebarHoverText; }}
            onMouseLeave={e => { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = "transparent"; el.style.color = theme.sidebarText; }}
          >
            <Palette className="w-4 h-4" /> Appearance
          </button>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
            style={{ color: theme.sidebarText }}
            onMouseEnter={e => { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = "#fee2e2"; el.style.color = "#dc2626"; }}
            onMouseLeave={e => { const el = e.currentTarget as HTMLButtonElement; el.style.backgroundColor = "transparent"; el.style.color = theme.sidebarText; }}
          >
            <LogOut className="w-4 h-4" /> Log Out
          </button>
          {themeOpen && (
            <div className="absolute bottom-full left-3 right-3 mb-2 bg-white rounded-xl border border-slate-200 shadow-xl p-4 z-50 max-h-[80vh] overflow-y-auto" style={{ color: "#0f172a" }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold">Appearance</h3>
                <button onClick={() => setThemeOpen(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Sidebar</h4>
                  <div className="space-y-3">
                    <ColorRow label="Background" value={theme.sidebarBg} onChange={v => setTheme(t => ({ ...t, sidebarBg: v }))} swatches={["#ffffff", "#f8fafc", "#1e293b", "#0f172a", "#1e3a8a", "#0c4a6e", "#064e3b", "#3b0764"]} />
                    <ColorRow label="Text" value={theme.sidebarText} onChange={v => setTheme(t => ({ ...t, sidebarText: v }))} swatches={["#475569", "#0f172a", "#ffffff", "#e2e8f0", "#cbd5e1", "#93c5fd"]} />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Hover state</h4>
                  <div className="space-y-3">
                    <ColorRow label="Hover background" value={theme.sidebarHoverBg} onChange={v => setTheme(t => ({ ...t, sidebarHoverBg: v }))} swatches={["#f1f5f9", "#e2e8f0", "#1e3a8a", "#312e81", "#334155", "#7c3aed", "#0e7490", "#15803d"]} />
                    <ColorRow label="Hover text" value={theme.sidebarHoverText} onChange={v => setTheme(t => ({ ...t, sidebarHoverText: v }))} swatches={["#0f172a", "#1d4ed8", "#ffffff", "#fbbf24", "#34d399", "#f472b6"]} />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Selected item</h4>
                  <div className="space-y-3">
                    <ColorRow label="Selected background" value={theme.sidebarActiveBg} onChange={v => setTheme(t => ({ ...t, sidebarActiveBg: v }))} swatches={["#1d4ed8", "#2563eb", "#0ea5e9", "#7c3aed", "#db2777", "#059669", "#dc2626", "#0f172a"]} />
                    <ColorRow label="Selected text" value={theme.sidebarActiveText} onChange={v => setTheme(t => ({ ...t, sidebarActiveText: v }))} swatches={["#ffffff", "#0f172a", "#fbbf24", "#fef3c7", "#e0f2fe", "#fce7f3"]} />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Main area</h4>
                  <ColorRow label="Background" value={theme.mainBg} onChange={v => setTheme(t => ({ ...t, mainBg: v }))} swatches={["#f8fafc", "#ffffff", "#f1f5f9", "#fef3c7", "#fce7f3", "#e0e7ff", "#dcfce7", "#0f172a"]} />
                </div>
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <button onClick={() => setTheme(THEME_DEFAULTS)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-lg border border-slate-200 hover:bg-slate-50">
                    <RotateCcw className="w-3 h-3" /> Reset to default
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 leading-snug">Saved to your account. Your appearance will follow you to any browser you sign in from.</p>
              </div>
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 md:px-6 h-14 flex items-center justify-between gap-2">
            <button onClick={() => setMobileNavOpen(true)} className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-slate-100 text-foreground" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-base md:text-lg font-bold text-foreground flex-1 min-w-0 truncate">
              {sidebarView === "home" ? "Dashboard" : sidebarView === "forms" ? "Forms" : sidebarView === "ppm" ? "PPM Schedules" : sidebarView === "callouts" ? "Schedules" : sidebarView === "clients" ? "Domestic" : sidebarView === "company" ? "Commercial" : sidebarView === "calendar" ? "Calendar" : sidebarView === "profile" ? "My Profile" : sidebarView === "nl-dashboard" ? "Newsletter · Dashboard" : sidebarView === "nl-subscribers" ? "Newsletter · Subscribers" : sidebarView === "nl-templates" ? "Newsletter · Email Templates" : sidebarView === "nl-campaigns" ? "Newsletter · Campaigns" : sidebarView === "nl-smtp" ? "Newsletter · SMTP Test" : sidebarView === "nl-reports" ? "Newsletter · Reports" : sidebarView === "nl-settings" ? "Newsletter · Settings" : sidebarView === "pages" ? "Pages" : "Website Analytics"}
            </h1>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {sidebarView === "analytics" && (
                <>
                  <div className="hidden sm:flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-hidden mr-2">
                    <button onClick={() => setAnalyticsTab("overview")} className={`px-3 py-1.5 text-xs font-medium transition-all ${analyticsTab === "overview" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>Overview</button>
                    <button onClick={() => setAnalyticsTab("visitors")} className={`px-3 py-1.5 text-xs font-medium transition-all ${analyticsTab === "visitors" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>Visitor Journeys</button>
                  </div>
                  <div className="hidden md:flex items-center gap-2">
                    {[7, 14, 30, 90].map(d => (
                      <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${days === d ? "bg-primary text-white shadow-sm" : "bg-white border border-slate-200 text-muted-foreground hover:border-slate-300"}`}>{d}d</button>
                    ))}
                  </div>
                </>
              )}
              <button onClick={() => sidebarView === "forms" ? fetchSubmissions() : sidebarView === "analytics" ? fetchAnalytics() : setCalendarKey(k => k + 1)}
                className="p-2 rounded-lg hover:bg-slate-100 text-muted-foreground hover:text-foreground transition-colors" title="Refresh">
                <RefreshCw className="w-4 h-4" />
              </button>
              <button onClick={() => { setSidebarView("forms"); setNotifOpen(false); }}
                className="relative p-2 rounded-lg hover:bg-slate-100 text-muted-foreground hover:text-foreground transition-colors" title={`${submissions.length} form submissions`}>
                <Bell className="w-4 h-4" />
                {submissions.length > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                    {submissions.length > 99 ? "99+" : submissions.length}
                  </span>
                )}
              </button>
              <div className="relative">
                <button onClick={() => setProfileOpen(o => !o)}
                  className="flex items-center gap-2 p-1 pr-2 rounded-lg hover:bg-slate-100 transition-colors" title="Account">
                  {me?.avatar ? (
                    <img src={me.avatar} alt="" className="w-8 h-8 rounded-full object-cover shadow-sm" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-sky-600 text-white flex items-center justify-center font-bold text-xs shadow-sm uppercase">
                      {(me?.name || "A").trim().split(/\s+/).map(p => p[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${profileOpen ? "rotate-180" : ""}`} />
                </button>
                {profileOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setProfileOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-xl border border-slate-200 z-40 overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <div className="text-sm font-semibold text-foreground">{me?.name || "Administrator"}</div>
                        <div className="text-xs text-muted-foreground truncate">{me?.email || "VertexAir Admin Panel"}</div>
                      </div>
                      <button onClick={() => { setProfileOpen(false); setSidebarView("profile"); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                        <UserCircle2 className="w-4 h-4" /> My Profile
                      </button>
                      <button onClick={() => { setProfileOpen(false); handleLogout(); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors border-t border-slate-100">
                        <LogOut className="w-4 h-4" /> Log out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6">
          {sidebarView === "home" && (
            <HomeView submissionCount={submissions.length} onNavigate={setSidebarView} />
          )}

          {sidebarView === "ppm" && <PpmOverview />}
          {sidebarView === "callouts" && <SchedulesOverview />}
          {sidebarView === "profile" && <ProfileView me={me} onUpdated={(m) => setMe(m)} />}

          {sidebarView === "forms" && (
            <>
              <div className="grid grid-cols-3 gap-4 mb-8">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${activeTab === tab.key ? "border-primary bg-primary/5 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${activeTab === tab.key ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500"}`}><Icon className="w-5 h-5" /></div>
                      <div className="text-left">
                        <p className={`text-2xl font-bold ${activeTab === tab.key ? "text-primary" : "text-foreground"}`}>{tab.count}</p>
                        <p className="text-xs text-muted-foreground font-medium">{tab.label}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {loading ? (
                <div className="text-center py-20 text-muted-foreground">Loading submissions...</div>
              ) : activeSubs.length === 0 ? (
                <div className="text-center py-20">
                  <div className="text-muted-foreground text-lg">No {tabs.find(t => t.key === activeTab)?.label.toLowerCase()} yet</div>
                  <p className="text-sm text-muted-foreground mt-2">Submissions will appear here when customers use your forms.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeSubs.map(sub => (
                    <SubmissionCard key={sub.id} sub={sub} onDelete={handleDelete} onUpdate={handleUpdate} onSchedule={s => { setScheduleSubmission(s); }} />
                  ))}
                </div>
              )}

              <div className="grid grid-cols-5 gap-4 mt-8">
                <StatCard label="Total Submissions" value={submissions.length} icon={FileText} color="bg-blue-50 text-blue-600" />
                <StatCard label="New" value={submissions.filter(s => !s.status || s.status === "new").length} icon={Mail} color="bg-sky-50 text-sky-600" />
                <StatCard label="In Progress" value={submissions.filter(s => s.status === "in_progress").length} icon={Clock} color="bg-amber-50 text-amber-600" />
                <StatCard label="Completed" value={submissions.filter(s => s.status === "completed").length} icon={Check} color="bg-emerald-50 text-emerald-600" />
                <StatCard label="Rejected" value={submissions.filter(s => s.status === "rejected").length} icon={X} color="bg-red-50 text-red-600" />
              </div>
            </>
          )}

          {sidebarView === "clients" && <ClientsView />}

          {sidebarView === "company" && <CompanyView />}

          {sidebarView === "calendar" && <CalendarView key={calendarKey} />}

          {sidebarView === "pages" && <AdminPages />}
          {sidebarView === "collections" && <AdminCollections />}
          {sidebarView === "templates" && <AdminProgrammatic />}

          {(sidebarView as string).startsWith("nl-") && (
            <AdminNewsletter
              view={(sidebarView as string).slice(3) as any}
              onNavigate={(v) => setSidebarView(`nl-${v}` as SidebarView)}
            />
          )}

          {sidebarView === "analytics" && (
            <>
              {analyticsLoading ? (
                <div className="text-center py-20 text-muted-foreground">Loading analytics...</div>
              ) : !analyticsData ? (
                <div className="text-center py-20 text-muted-foreground">No data available</div>
              ) : analyticsTab === "overview" ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    <StatCard label="Total Page Views" value={analyticsData.totalViews.toLocaleString()} icon={Eye} color="bg-blue-50 text-blue-600" />
                    <StatCard label="Unique Visitors" value={analyticsData.uniqueVisitors.toLocaleString()} icon={Users} color="bg-emerald-50 text-emerald-600" />
                    <StatCard label="Avg. Views / Day" value={avgViewsPerDay} icon={TrendingUp} color="bg-amber-50 text-amber-600" />
                    <StatCard label="Call Clicks" value={callClicks} icon={Phone} color="bg-orange-50 text-orange-600" />
                    <StatCard label="Pages Tracked" value={analyticsData.pages.length} icon={Globe} color="bg-purple-50 text-purple-600" />
                  </div>

                  <ChartCard title={`Visitors & Page Views (Last ${days} Days)`} icon={TrendingUp}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analyticsData.daily} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                        <defs>
                          <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} /><stop offset="95%" stopColor="#2563eb" stopOpacity={0} /></linearGradient>
                          <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.15} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                        <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: "13px" }} />
                        <Area type="monotone" dataKey="views" name="Page Views" stroke="#2563eb" strokeWidth={2} fill="url(#colorViews)" />
                        <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#10b981" strokeWidth={2} fill="url(#colorVisitors)" />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "13px" }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  <div className="grid lg:grid-cols-2 gap-6">
                    <ChartCard title="Traffic Sources" icon={Globe}>
                      {analyticsData.sources.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={analyticsData.sources} cx="50%" cy="45%" innerRadius={60} outerRadius={95} paddingAngle={3} dataKey="value" strokeWidth={0}>
                              {analyticsData.sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Legend verticalAlign="bottom" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "13px", color: "#64748b" }} />
                            <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: "13px" }} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data yet</div>}
                    </ChartCard>

                    <ChartCard title="Top Pages" icon={BarChart3}>
                      {analyticsData.pages.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={analyticsData.pages.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} width={100} />
                            <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: "13px" }} />
                            <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} name="Views" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data yet</div>}
                    </ChartCard>

                    {analyticsData.events.length > 0 && (
                      <ChartCard title="Events (Call Clicks & Actions)" icon={MousePointerClick}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={analyticsData.events.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} width={160} />
                            <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: "13px" }} />
                            <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Clicks" />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartCard>
                    )}

                    <ChartCard title="Devices" icon={Monitor}>
                      {analyticsData.devices.length > 0 ? (
                        <div className="h-full flex flex-col justify-center">
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie data={analyticsData.devices} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value" strokeWidth={0}>
                                {analyticsData.devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                              </Pie>
                              <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "13px" }} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="flex justify-center gap-6 mt-2">
                            {analyticsData.devices.map((d, i) => {
                              const Icon = DEVICE_ICONS[d.name] || Monitor;
                              const pct = Math.round((d.value / analyticsData.totalViews) * 100);
                              return (
                                <div key={d.name} className="flex items-center gap-2 text-sm">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                  <Icon className="w-4 h-4 text-muted-foreground" /><span className="text-foreground font-medium capitalize">{d.name}</span><span className="text-muted-foreground">{pct}%</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data yet</div>}
                    </ChartCard>

                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <div className="flex items-center gap-2 mb-4"><Globe className="w-4 h-4 text-primary" /><h3 className="font-semibold text-foreground text-sm">Browsers & Operating Systems</h3></div>
                      <div className="grid grid-cols-2 gap-6 h-[280px]">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Browsers</p>
                          <div className="space-y-2.5">
                            {analyticsData.browsers.slice(0, 6).map((b, i) => {
                              const pct = Math.round((b.value / analyticsData.totalViews) * 100);
                              return (
                                <div key={b.name}>
                                  <div className="flex items-center justify-between text-sm mb-1"><span className="text-foreground font-medium">{b.name}</span><span className="text-muted-foreground">{b.value} ({pct}%)</span></div>
                                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} /></div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Operating Systems</p>
                          <div className="space-y-2.5">
                            {analyticsData.os.slice(0, 6).map((o, i) => {
                              const pct = Math.round((o.value / analyticsData.totalViews) * 100);
                              return (
                                <div key={o.name}>
                                  <div className="flex items-center justify-between text-sm mb-1"><span className="text-foreground font-medium">{o.name}</span><span className="text-muted-foreground">{o.value} ({pct}%)</span></div>
                                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: COLORS[(i + 3) % COLORS.length] }} /></div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-1"><Route className="w-4 h-4 text-primary" /><h3 className="font-semibold text-foreground">Visitor Journeys</h3></div>
                    <p className="text-sm text-muted-foreground mb-4">See which pages each visitor viewed and their actions (call clicks shown in orange).</p>
                    {analyticsData.visitors.length > 0 ? (
                      <div className="space-y-2">{analyticsData.visitors.map((v, i) => <VisitorRow key={i} visitor={v} />)}</div>
                    ) : <div className="text-center py-12 text-muted-foreground">No visitor data yet.</div>}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <ScheduleModal
        submission={scheduleSubmission}
        onClose={() => setScheduleSubmission(null)}
        onCreated={() => { setCalendarKey(k => k + 1); alert("Appointment added to the calendar."); }}
      />
    </div>
  );
}

function ColorRow({ label, value, onChange, swatches }: { label: string; value: string; onChange: (v: string) => void; swatches: string[] }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <div className="relative flex-shrink-0">
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer bg-white" style={{ padding: 0 }} aria-label={`${label} color picker`} />
        </div>
        <input
          type="text"
          value={value}
          onChange={e => { const v = e.target.value; if (/^#[0-9a-fA-F]{0,6}$/.test(v) || v === "") onChange(v); }}
          className="flex-1 px-2 py-1.5 text-xs font-mono uppercase rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
          maxLength={7}
          placeholder="#ffffff"
        />
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {swatches.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            className={`w-6 h-6 rounded-md border-2 transition-transform hover:scale-110 ${value.toLowerCase() === s.toLowerCase() ? "border-primary ring-2 ring-primary/30" : "border-slate-200"}`}
            style={{ backgroundColor: s }}
            aria-label={`Use ${s}`}
            title={s}
          />
        ))}
      </div>
    </div>
  );
}
